import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useTenantData } from '../context/TenantDataContext';
import { useAuth } from '../context/AuthContext';
import { TileMenu, PanelCard, PanelAlert, PanelRec, PanelItems } from '../components/StatusCards';
import { usePanelOpen } from '../hooks/usePanelOpen';
import { usePersistentSet } from '../hooks/usePersistentState';
import { permissionLevel } from '../components/DetailTabs';
import { permissionDescription } from '../data/permissionText';
import { useLicense } from '../hooks/useLicense';
import { useCachedFetch } from '../hooks/useCachedFetch';
import { listAuditNotes, createAuditNote } from '../api';
import { DECISION_TYPES } from '../components/DecisionTypeRadios';
import OwnerPicker from '../components/OwnerPicker';
import { getStatusBadge } from '../utils/statusBadge';
import { useResizablePanel } from '../hooks/useResizablePanel';
import { useItList } from '../hooks/useItList';
import ExpandAll from '../components/ExpandAll';
import StatusFrame from '../components/StatusFrame';
import { isSettledNote } from '../utils/decisionStatus';
import FocusToggle, { useFocusMode } from '../components/FocusToggle';
import FixPath from '../components/FixPath';
import '../components/ItListCard.css';
import { useColumnWidths } from '../hooks/useColumnWidths';
import SearchInput from '../components/SearchInput';
import LoadingState from '../components/LoadingState';
import NcIcon from '../components/NcIcon';
import DatePicker from '../components/DatePicker';
import { AREA_ICON, areaLabel, areaTabLabel } from '../data/areaLabels';
import { FINDING_RISK } from '../data/findingRisks';
import DecisionTag from '../components/DecisionTag';
import PortalIcon from '../components/PortalIcon';
import { findingTitle } from '../utils/findingLabels';
import { Val } from '../components/Missing';
import { DataBlock } from '../components/DetailPanel';
import './WorkQueueGroups.css';
import './WorkQueueCards.css';
import { displayCaPolicyName } from '../utils/caPolicyName';
import { RulePanel, CleanupNote } from '../components/DetailTabs';
import { useFindingGroups, daysSince, canonicalFindingKey, settingText, dkimText } from '../hooks/useFindingGroups';
import { exportToExcel, exportToPdf } from '../utils/exportList';
import { deviceOverview, policyNames } from '../utils/policyResults';
import './styles.css';

// Tabel mode's area tabs — one page, two views (Fund + Tabel) sharing this
// same list of areas, never a subset. The actual per-area inventory Tabel
// renders is built by tabelItems below, from the same raw tenant data the
// fund groups above already consume — not by mounting each area's own
// page (that read as visually "a different page" nested inside this one).
// Where you were in Arbejdskø (area, finding, object) is remembered for the
// session, so coming back from Beslutninger or the report lands on the same spot.
const wqStateKey = (id) => `nc_wq_state:${id}`;
function readWqState(id) {
  try {
    return JSON.parse(sessionStorage.getItem(wqStateKey(id)) || 'null') || {};
  } catch {
    return {};
  }
}

// How many of a finding's objects its card shows before Vis alle.
const FUND_ROWS = 4;
const AREA_KEYS = ['identity', 'devices', 'security', 'exchange', 'sharepoint', 'applications', 'roles', 'conditional-access', 'licenses'];

// Caps how many Tabel rows render at once — "Alle områder" on a large
// tenant can otherwise mean thousands of DOM rows in one list.
const TABEL_PAGE_SIZE = 150;

// The area's name in the page eyebrow ("Gennemgang - Brugere"): the same name as everywhere else on the site.
// Each area has its own colour in the tiles (the product it comes from), whatever it holds.
const AREA_TILE_COLOR = {
  alle: ['#ECEAF3', '#4B4A66'],
  identity: ['#EDEBFC', '#5D5FE8'],
  devices: ['#E3F0FD', '#1B6FD1'],
  security: ['#FFF0DC', '#D9760A'],
  exchange: ['#DFF3EA', '#157F5C'],
  sharepoint: ['#DDF3F5', '#0E7C86'],
  applications: ['#E7ECFB', '#3F51B5'],
  roles: ['#FBEFD0', '#B7791F'],
  'conditional-access': ['#F6E6F7', '#A33CB8'],
  licenses: ['#E8EDF2', '#51627A'],
};

const MS365_AREA_NAME = {
  identity: 'Brugere',
  devices: 'Enheder',
  security: 'Sikkerhed',
  exchange: 'E-mail',
  sharepoint: 'Grupper',
  applications: 'Entra ID Apps',
  roles: 'Roller',
  'conditional-access': 'Loginregler',
  licenses: 'Licenser',
};

// Microsoft Graph's partnerReportedThreatState enum, translated — 'unknown'
// is the one value treated elsewhere as "not reporting to Defender" (see
// useFindingGroups.js/DeviceDetail.jsx's noThreatAgent), everything else is
// a real reported state worth showing as-is rather than a raw English enum.
const DEFENDER_STATUS_LABEL = {
  unknown: 'Intet signal modtaget endnu',
  activated: 'Aktiv og rapporterer',
  deactivated: 'Deaktiveret',
  secured: 'Sikret',
  lowSeverity: 'Lav alvorlighed registreret',
  mediumSeverity: 'Middel alvorlighed registreret',
  highSeverity: 'Høj alvorlighed registreret',
  unresponsive: 'Svarer ikke',
  compromised: 'Kompromitteret',
  misconfigured: 'Forkert konfigureret',
};

// One-line explanation under each decision type in the Dokumentation form,
// so the choice is self-explanatory without placeholder text.
const DECISION_HINT = {
  accepted: 'Vi kender risikoen og accepterer den bevidst.',
  temp: 'Afvigelsen er tilladt i en periode eller et særtilfælde.',
  compensating: 'En anden foranstaltning dækker risikoen.',
  falsepos: 'Fundet er ikke reelt. Objektet er i orden.',
};

// Object detail routes elsewhere use these exact prefixes for pageLabel —
// matching them here means a decision documented from Arbejdskø shows up
// identically in that object's own note history and in Beslutninger &
// risici, instead of a second, inconsistent label format.
const PAGE_LABEL_PREFIX = {
  identity: 'Bruger',
  devices: 'Enhed',
  exchange: 'Bruger',
  applications: 'Applikation',
  roles: 'Rolle',
  'conditional-access': 'Loginregel',
  security: 'Hændelse',
  sharepoint: 'SharePoint-site',
  licenses: 'Licens',
};

// The detail panel's fixed third tab, renamed per area (position and
// wording match every area now — "fast struktur, dynamiske data" per
// direct feedback) — a plain kv list for areas with no member-shaped data
// of their own, "Medlemmer"/"Adgang" for the areas that do.
// What the card with the facts about the object is called: by what the object IS, never by the area it stands in (E-mail holds domains and
// mailboxes, Sikkerhed incidents, alerts, sign-ins and users, Grupper sites and workspaces, Roller roles and the people who hold them).
// An object this does not know is "Om objektet", so the title is never wrong.
function factsTitleFor(finding, item) {
  const key = finding?.key || '';
  const nav = String(item?.navPath || '');
  let noun = null;
  if (/^(id-|ok-id-)/.test(key)) noun = 'brugeren';
  else if (/^(enheder-|ok-enheder)/.test(key)) noun = 'enheden';
  else if (/^(app-|ok-app-)/.test(key)) noun = 'appen';
  else if (key === 'ok-roller-mfa') noun = 'brugeren';
  else if (/^roller-/.test(key)) noun = 'rollen';
  else if (/^(exchange-domain|ok-exchange-domain)/.test(key)) noun = 'domænet';
  else if (/^(exchange-|ok-exchange-)/.test(key)) noun = 'postkassen';
  else if (/^(sp-|ok-sp-)/.test(key)) noun = nav.startsWith('sharepoint/') ? 'sitet' : 'workspacet';
  else if (/^(lic-|ok-lic-)/.test(key)) noun = 'licensen';
  else if (/^(ca-|ok-ca-)/.test(key)) noun = 'reglen';
  else if (key === 'sik-risky-signin') noun = 'loginet';
  else if (/^sik-(failed|legacy)/.test(key)) noun = 'brugeren';
  else if (/^sik-/.test(key)) noun = item?.sub === 'Incident' ? 'incidenten' : item?.sub === 'Alert' ? 'alarmen' : null;
  return noun ? `Om ${noun}` : 'Om objektet';
}
const PERM_LEVELS = [['full', 'Fuld kontrol', 'bad'], ['write', 'Kan ændre', 'warn'], ['read', 'Kan læse', 'ok'], ['other', 'Uklart niveau', undefined]];
const TAB3_LABEL = {
  identity: 'Medlemskab',
  devices: 'Politikker',
  security: 'Hændelsesdetaljer',
  exchange: 'Adgang',
  sharepoint: 'Medlemmer',
  applications: 'Tilladelser',
  roles: 'Indehavere',
  'conditional-access': 'Regel',
  licenses: 'Tildeling',
};

// Alle objekter's own fixed structure — per direct feedback, columns used
// to be "whichever 4 labels happen to appear first" (order could vary with
// data), and several of them weren't decision-relevant (Email, Sidst
// brugt, the full "Gælder for" text). Explicit list per area now, same
// idea as TAB3_LABEL: fixed positions, values still pulled live from meta.
const TABEL_COLUMNS = {
  identity: ['Kontostatus', 'Ekstra loginbeskyttelse', 'Licens', 'Seneste login'],
  devices: ['Styresystem', 'Compliance', 'Krypteret'],
  security: ['Alvorlighed', 'Sagsstatus'],
  exchange: ['Type', 'Videresendelse'],
  sharepoint: ['Type', 'Ejere', 'Medlemmer', 'Gæster', 'Seneste aktivitet'],
  applications: ['Tilladelser'],
  roles: ['Permanent', 'PIM-berettiget'],
  'conditional-access': ['Status', 'Kræver'],
  licenses: ['Tildelt', 'Købt', 'Ledige'],
};

// Exchange's own RecipientTypeDetails values, in plain Danish — same
// lookup handleMfaStatus/handleIdentityRisks use to exclude these from
// user-centric findings, exposed here as the E-mail area's own "Type"
// column instead. Falls back to the raw value for a type not in this
// list rather than showing nothing.
const RECIPIENT_TYPE_LABEL = {
  UserMailbox: 'Brugerpostkasse',
  SharedMailbox: 'Delt postkasse',
  RoomMailbox: 'Mødelokale',
  EquipmentMailbox: 'Udstyr',
  DiscoveryMailbox: 'Discovery',
  MailUser: 'Mailbruger',
  GuestMailUser: 'Gæst',
};

const NOT_AVAILABLE = 'Ikke tilgængeligt endnu';
// "Anna, Bo og 3 til" for a long list of names.
const nameListShort = (names) => { const u = [...new Set(names.filter(Boolean))]; return u.length <= 3 ? u.join(', ') : `${u.slice(0, 3).join(', ')} og ${u.length - 3} til`; };

function pluckMeta(meta, label) {
  return (meta || []).find(([k]) => k === label)?.[1];
}
function withoutMeta(meta, labels) {
  return (meta || []).filter(([k]) => !labels.includes(k));
}
// A joined name list ("Anna Berg, Peter Holm, ...") overflows Alle
// objekter's fixed-width columns and gets clipped unreadably — per direct
// feedback. Table cells show this short form instead; the full list still
// lives in the detail panel's own tab (Adgang/Medlemmer), and the row's
// `title` attribute (set where this is used) covers a hover glance too.
function summarizeNames(names) {
  if (!names || names.length === 0) return null;
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1} andre`;
}
// A row's 3rd tuple element: `true` = a real gap (red, "we don't collect
// this yet"), `'note'` = a known, non-fixable limitation (neutral, e.g. a
// license the customer's tenant may not have) — distinct styling so the
// two don't read as the same kind of problem.
// Rows for the shared settings block: a value we do not have yet shows as the red dash, 'hot' as orange.
// Tab-3 kinds that have a layout of their own; any other (a plain list of rows) is facts.
const SPECIAL_TAB3 = ['members', 'permissions', 'access', 'rule', 'devicePolicies', 'lists'];
const kvRows = (rows) => rows.map(([k, v, flag]) => [k, v === NOT_AVAILABLE ? <Val v={v} /> : v, false, flag === 'hot' ? true : flag === true ? 'gap' : false]);

// Splits one item's existing `meta` (Overblik today) into the new
// Overblik/[tab3]/Evidens shape — real fields move to whichever tab they
// fit best, nothing new is fabricated: a field we don't collect yet renders
// as NOT_AVAILABLE, same as everywhere else on the site. `owners`/
// `nonOwnerMembers` are only non-empty for sharepoint/roles today.
function buildDetailSections(area, meta, item, owners, nonOwnerMembers, policyCtx, extra = {}) {
  switch (area) {
    case 'identity':
      return {
        overblik: withoutMeta(meta, ['Ekstra loginbeskyttelse', 'Seneste login']),
        tab3: {
          kind: 'lists',
          sections: [
            {
              label: 'Entra-grupper',
              emptyHint: 'Ikke medlem af nogen gruppe.',
              items: (item?.groupsList || []).map((g) => [g.name, g.isOwner ? 'Ejer' : 'Medlem']),
            },
            {
              label: 'Administrative roller',
              emptyHint: 'Ingen administrative roller tildelt.',
              // An administrator role without MFA is what makes a missing second factor serious.
              items: (item?.rolesList || []).map((r) => [r.role, r.noMfa ? `${r.status}, uden MFA` : r.status]),
            },
          ],
        },
        evidens: [
          ['Loginbeskyttelse', pluckMeta(meta, 'Ekstra loginbeskyttelse') ?? NOT_AVAILABLE],
          ['Seneste login', pluckMeta(meta, 'Seneste login') ?? NOT_AVAILABLE],
          ['Adgangskode skiftet', item?.passwordAgeDays != null ? `${item.passwordAgeDays} dage siden` : NOT_AVAILABLE, item?.passwordAgeDays == null],
          // Not a missing-code gap like the red NOT_AVAILABLE rows — this is
          // a licensing fact about the customer's own tenant (Entra ID
          // Protection is a P2 feature), so it gets its own honest label and
          // a neutral, not alarming, style — see 'note' below.
          ['Risikobaseret loginbeskyttelse', 'Kræver Entra ID P2-licens', 'note'],
        ],
      };
    case 'devices': {
      // Every policy that targets this device, of every type — not just the two Graph gives a
      // per-device state for. Real evaluated state (Opfyldt/Ikke opfyldt/Afventer) where Intune
      // gives one (compliance + configuration, matched by name against the device's own
      // deviceCompliancePolicyStates/deviceConfigurationStates); "Tildelt" for the rest, because
      // Microsoft does not expose a pass/fail for those types.
      return {
        overblik: withoutMeta(meta, ['Krypteret']),
        // Security Baselines and Tildelte applikationer dropped — per
        // direct feedback, this product's baseline is Compliance Policies +
        // Configuration Profiles, not Intune's own separate "Security
        // Baselines" concept, and per-device app assignment isn't relevant
        // here.
        // Only the policies that fail are listed (what explains the finding); the rest is a count, and the
        // full list lives under Konfiguration, Politikker.
        tab3: {
          kind: 'devicePolicies',
          state: !policyCtx ? 'loading' : policyCtx.results ? 'ok' : 'missing',
          overview: policyCtx?.results ? deviceOverview(policyCtx.results, policyCtx.names, { id: item?.id, name: item?.name, states: [...(item?.compliancePolicyStates || []).map((s) => ({ ...s, source: 'compliance' })), ...(item?.configurationStates || []).map((s) => ({ ...s, source: 'configuration' }))] }) : null,
        },
        evidens: [
          ['Kryptering', pluckMeta(meta, 'Krypteret') ?? NOT_AVAILABLE, pluckMeta(meta, 'Krypteret') == null],
          [
            'Defender-status',
            item?.partnerReportedThreatState
              ? DEFENDER_STATUS_LABEL[item.partnerReportedThreatState] || item.partnerReportedThreatState
              : NOT_AVAILABLE,
            item?.partnerReportedThreatState == null,
          ],
        ],
      };
    }
    case 'security': {
      const roles = item?.affectedUserId ? (extra.rolesByUserId?.[item.affectedUserId] || []).map((r) => r.role) : null;
      return {
        overblik: withoutMeta(meta, ['Alvorlighed']),
        tab3: {
          rows: [
            ['Type', item?.sub || NOT_AVAILABLE],
            ['Alvorlighed', pluckMeta(meta, 'Alvorlighed') || NOT_AVAILABLE],
            // For a sign-in finding the person it is about, and what that account can do.
            ...(item?.affectedUserId ? [['Berørt bruger', item.name], ['Administratorrolle', roles && roles.length ? roles.join(', ') : 'Ingen']] : []),
          ],
        },
        // Real Defender-returned fields now, replacing the old placeholder
        // "Rå hændelsesdata" row (which never corresponded to anything we
        // actually fetched). Classification/tildeling absent is a real
        // "not set in Defender yet" answer, not a data gap on our side.
        evidens: [
          ['Beskrivelse', item?.description || NOT_AVAILABLE, !item?.description],
          ['Klassificering', item?.determination || 'Ikke klassificeret endnu'],
          ['Tildelt', item?.assignedTo || 'Ikke tildelt'],
          ['Sidst opdateret', item?.lastUpdateDateTime ? `${daysSince(item.lastUpdateDateTime)} dage siden` : NOT_AVAILABLE],
        ],
      };
    }
    case 'exchange': {
      // A domain finding (SPF/DMARC) is about a DNS record, not a mailbox — it has no access lists.
      if (item?.domainAuth) {
        const d = item.domainAuth;
        return {
          overblik: meta,
          tab3: { rows: [['Domæne', d.domain], ['SPF', d.spf?.configured ? 'Konfigureret' : 'Mangler'], ['DKIM', dkimText(d)], ['DMARC', d.dmarc?.configured ? `Konfigureret, politik: ${d.dmarc.policy || 'ukendt'}` : 'Mangler']] },
          evidens: [
            ['SPF-record', d.spf?.record || 'Ingen record fundet'],
            ...(d.dkim?.records?.length > 0 ? d.dkim.records.map((r) => [`DKIM-record (${r.name.split('._domainkey')[0]})`, r.value]) : [['DKIM-record', dkimText(d) === 'Ikke tilgængeligt endnu' ? NOT_AVAILABLE : 'Ingen record fundet']]),
            ['DMARC-record', d.dmarc?.record || 'Ingen record fundet'],
          ],
        };
      }
      // fullAccess/sendAs are null when we haven't been able to look them
      // up at all (Exchange not connected, or this one mailbox's lookup
      // failed) — a real empty array ("nobody else has access") is a
      // genuine answer, not a gap, so only null gets NOT_AVAILABLE.
      const fullAccessList = item?.fullAccess;
      const sendAsList = item?.sendAs;
      const sendOnBehalfList = item?.sendOnBehalfTo || [];
      const forwardsTo = item?.forwardsTo || [];
      const hasForwardRow = (meta || []).some(([k]) => k === 'Videresendes til');
      return {
        // One row for where the mail goes, not a vague "Videresendelse" row next to it.
        overblik: [...(meta || []).filter(([k]) => !(forwardsTo.length > 0 && k === 'Videresendelse')), ...(forwardsTo.length > 0 && !hasForwardRow ? [['Videresendes til', forwardsTo.join(', '), 'hot']] : [])],
        tab3: {
          kind: 'access',
          count: forwardsTo.length + (fullAccessList || []).length + (sendAsList || []).length + sendOnBehalfList.length,
          sections: [
            ...(forwardsTo.length > 0 ? [{ label: 'Videresender post til', items: forwardsTo.map((name) => ({ name, sub: 'Uden for organisationen' })) }] : []),
            { label: 'Kan læse hele postkassen', missing: fullAccessList == null, items: (fullAccessList || []).map((name) => ({ name, sub: 'Full Access' })) },
            { label: 'Kan sende som ejeren', missing: sendAsList == null, items: (sendAsList || []).map((name) => ({ name, sub: 'Send As' })) },
            { label: 'Kan sende på vegne af ejeren', items: sendOnBehalfList.map((name) => ({ name, sub: 'Send on Behalf' })) },
          ],
        },
        evidens: [['Mailbox Settings', forwardsTo.length > 0 ? 'Ekstern videresendelse er slået til' : pluckMeta(meta, 'Videresendelse') || NOT_AVAILABLE]],
      };
    }
    case 'sharepoint': {
      const lp = item?.lifecyclePolicy;
      // Guests get their own highlighted row — external people with access
      // is what an auditor asks about first.
      const guestRow = (k, v) => (k === 'Gæster' && Number(v) > 0 ? [k, v, 'hot'] : [k, v]);
      const isWorkspace = !!item && Object.prototype.hasOwnProperty.call(item, 'lifecyclePolicy');
      return {
        overblik: [...(pluckMeta(meta, 'Type') ? [] : [['Type', item?.sub || NOT_AVAILABLE]]), ...(meta || []).map(([k, v]) => guestRow(k, v))],
        tab3: { kind: 'members', owners: owners || [], members: nonOwnerMembers || [], count: (owners || []).length + (nonOwnerMembers || []).length },
        evidens: item?.visibility ? [['Synlighed', item.visibility === 'Public' ? 'Åben for alle i organisationen' : 'Privat', item.visibility === 'Public' ? 'hot' : undefined]] : [],
        cleanup: isWorkspace ? { policy: lp || null } : null,
      };
    }
    case 'applications':
      return {
        overblik: [['Udgiver', item?.sub || NOT_AVAILABLE], ...withoutMeta(meta, ['Tilladelser'])],
        tab3: { kind: 'permissions', permissions: item?.permissions || [], permissionCount: item?.permissionCount ?? null, count: (item?.permissions || []).length || item?.permissionCount || 0 },
        evidens: [
          ['Verificeret udgiver', item?.hasVerifiedPublisher === undefined || item?.hasVerifiedPublisher === null ? NOT_AVAILABLE : item.hasVerifiedPublisher ? 'Ja' : 'Nej', item?.hasVerifiedPublisher === undefined || item?.hasVerifiedPublisher === null ? true : item.hasVerifiedPublisher ? undefined : 'hot'],
          ['Certifikatstatus', item?.certificateStatus || NOT_AVAILABLE, !item?.certificateStatus],
          ['Secret nøgle', item?.secretExpiry || NOT_AVAILABLE, !item?.secretExpiry],
          // Who answers for the app, and who may use it. Not read yet is said so; no owner at all is worth a mark.
          ['Ejere', item?.owners == null ? NOT_AVAILABLE : item.owners.length === 0 ? 'Ingen ejer registreret' : nameListShort(item.owners.map((o) => o.name)), item?.owners == null ? true : item.owners.length === 0 ? true : undefined],
          ['Tildelt', item?.appMembers == null ? NOT_AVAILABLE : item.appMembers.length === 0 ? 'Ingen brugere eller grupper' : `${item.appMembers.length} · ${nameListShort(item.appMembers.map((m) => m.name))}`, item?.appMembers == null],
        ],
      };
    case 'roles':
      return {
        overblik: meta,
        tab3: { kind: 'members', hideOwners: true, owners: [], members: nonOwnerMembers || [] },
        evidens: [['PIM-aktiveringshistorik', NOT_AVAILABLE, true]],
      };
    case 'conditional-access': {
      return {
        overblik: [
          ['Tilstand', pluckMeta(meta, 'Status') || NOT_AVAILABLE],
          ['Oprettet', item?.createdDateTime ? new Date(item.createdDateTime).toLocaleDateString('da-DK', { dateStyle: 'long' }) : NOT_AVAILABLE, !item?.createdDateTime],
          ['Sidst ændret', item?.modifiedDateTime ? new Date(item.modifiedDateTime).toLocaleDateString('da-DK', { dateStyle: 'long' }) : NOT_AVAILABLE, !item?.modifiedDateTime],
        ],
        tab3: { kind: 'rule', item },
        evidens: [],
      };
    }
    case 'licenses':
      return {
        overblik: meta,
        tab3: {
          kind: 'members',
          hideOwners: true,
          membersLabel: 'Tildelte brugere',
          owners: [],
          members: (item?.users || []).map((u) => ({ id: u.id, displayName: u.displayName, userPrincipalName: u.userPrincipalName })),
        },
        evidens: [
          [
            'Servicepakker',
            (item?.servicePlans || []).length > 0 ? `${item.servicePlans.filter((sp) => sp.enabled).length} af ${item.servicePlans.length} aktive` : NOT_AVAILABLE,
            (item?.servicePlans || []).length === 0,
          ],
        ],
      };
    default:
      return { overblik: meta, tab3: { rows: [['Detaljer', NOT_AVAILABLE, true]] }, evidens: [['Detaljer', NOT_AVAILABLE, true]] };
  }
}

// One folded card of the side panel: a grey title strip with a count, and either a one-line summary (quiet) or the
// whole content (full colour) once the strip is pressed.
export default function WorkQueuePage() {
  const { tenant, mfa, devices, ca, applications, membership, security, loading } = useTenantData();
  const license = useLicense();
  const { user } = useAuth();
  // Same tenant-members lookup Beslutninger & risici's own Ejer field uses, so
  // "Ejer" is a real, searchable person here too — not a free-text field that
  // can drift from what the audit trail elsewhere calls that same person.
  const { data: membersData } = useCachedFetch(tenant ? `/api/tenant-members?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const ownerMembers = useMemo(() => {
    const list = membersData?.members || [];
    if (user?.email && !list.some((m) => m.email === user.email)) {
      return [{ email: user.email, name: user.name || user.email, role: user.role }, ...list];
    }
    return list;
  }, [membersData, user]);
  const { data: risks, loading: risksLoading } = useCachedFetch(tenant ? `/api/identity-risks?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const { data: insightsData, loading: insightsLoading } = useCachedFetch(tenant ? `/api/exchange-insights?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const insights = insightsData?.error ? null : insightsData;
  const { data: licensesResult, loading: licensesLoading } = useCachedFetch(tenant ? `/api/licenses?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const licenses = Array.isArray(licensesResult?.licenses) ? licensesResult.licenses : null;
  const { data: sitesResult, loading: sitesLoading } = useCachedFetch(tenant ? `/api/sharepoint-sites?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const sites = Array.isArray(sitesResult?.sites) ? sitesResult.sites : null;
  // "Filer & samarbejde" covers Microsoft 365-grupper and their Teams too,
  // not just SharePoint sites — membership is what actually needs
  // documenting for these, per direct feedback. /api/groups returns two
  // separate things: `groups` (plain Security/Distribution groups, no
  // Team/site to correlate) and `workspaces` (Microsoft 365 groups, each
  // already correlated with its own Team/SharePoint site server-side, so
  // the same workspace never shows up twice under two different names).
  const { data: groupsResult, loading: groupsLoading } = useCachedFetch(tenant ? `/api/groups?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const groups = Array.isArray(groupsResult?.groups) ? groupsResult.groups : null;
  // Which policies target a given device/user — same data Politikker already fetches and caches,
  // read again here (not re-fetched from Microsoft; withCache/useCachedFetch share the answer).
  const { data: policyInventory } = useCachedFetch(tenant ? `/api/policy-inventory?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  const policyResultsReq = useCachedFetch(tenant ? `/api/policy-results?tenantId=${tenant.tenantId}` : null, { skip: !tenant });
  // null while the policy list or the results are still on their way; { results: null } once they have
  // failed (shown as "Ikke tilgængeligt endnu", never as "all fine").
  const policyCtx = useMemo(() => {
    if (!policyInventory || policyInventory.error) return null;
    const results = policyResultsReq.data && !policyResultsReq.data.error ? policyResultsReq.data : null;
    if (!results && policyResultsReq.loading) return null;
    return { names: policyNames(policyInventory), results };
  }, [policyInventory, policyResultsReq.data, policyResultsReq.loading]);
  const workspaces = Array.isArray(groupsResult?.workspaces) ? groupsResult.workspaces : null;
  const lifecyclePolicy = groupsResult?.lifecyclePolicy || null;

  // The same finding-groups logic Oversigt.jsx's "Start Her" summary uses
  // for its own total — one shared place so the two pages can never show
  // a different number for the same tenant again.
  const { allGroups, nonEmptyGroups, okGroups } = useFindingGroups({ mfa, risks, ca, devices, membership, insights, applications, security, sites, licenses, workspaces });

  // Same lookup IdentitetAdgang.jsx builds for its own "Licens" column —
  // reused here so the detail panel can show a real assigned license
  // instead of leaving it out for lack of a per-user field.
  const licensesByUserId = useMemo(() => {
    const map = {};
    (licenses || []).forEach((sku) => {
      (sku.users || []).forEach((u) => {
        if (!map[u.id]) map[u.id] = [];
        map[u.id].push(sku.name);
      });
    });
    return map;
  }, [licenses]);

  // Same workspaces/groups + privilegedRoles data 'sharepoint'/'roles'
  // already fetch, just inverted to "which groups/roles is THIS user in" —
  // feeds Brugere's new Medlemskaber tab without a new API call.
  const groupsByUserId = useMemo(() => {
    const map = {};
    const addAll = (list) => (list || []).forEach((g) => {
      (g.members || []).forEach((m) => {
        if (!map[m.id]) map[m.id] = [];
        map[m.id].push({ name: g.displayName, isOwner: !!m.isOwner });
      });
    });
    addAll(workspaces);
    addAll(groups);
    return map;
  }, [workspaces, groups]);
  const rolesByUserId = useMemo(() => {
    const map = {};
    (risks?.privilegedRoles || []).forEach((r) => {
      const uid = r.principalId || r.id;
      if (!uid) return;
      if (!map[uid]) map[uid] = [];
      map[uid].push({ role: r.role || 'Ukendt rolle', status: r.status === 'eligible' ? 'PIM-berettiget' : 'Permanent' });
    });
    return map;
  }, [risks]);

  // 'fund' = the triage board below; 'alle' = the full per-area inventory
  // that used to live on its own "Detaljeret visning" page. Mirrored into
  // the URL (?view=alle).
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get('view') === 'alle' ? 'alle' : 'fund';
  function setViewMode(next) {
    // "Alle områder" isn't offered as an area tab in Alle objekter (see
    // dv-tabs-stack below) — landing there with areaFilter still 'alle'
    // would leave no tab showing as active and no way back to it, so
    // switch to a real area first.
    if (next === 'alle' && areaFilter === 'alle') setAreaFilter('identity');
    // Previously replaced the ENTIRE query string with just { view: 'alle' }
    // (or {}), which silently dropped any other param already in the URL —
    // most importantly ?area=, the one Historik's "Åbn" links set to land
    // on a specific area. Any navigation that ended up calling this rewrote
    // the URL to a bare ?view=alle with no area, which then rendered
    // blank. Merging with the previous params instead means this can only
    // ever change `view`, never anything else already there.
    setSearchParams((prev) => {
      const merged = new URLSearchParams(prev);
      if (next === 'alle') merged.set('view', 'alle');
      else merged.delete('view');
      return merged;
    }, { replace: true });
  }
  // Defaults to "Alle" status, per direct feedback — Fund and Alle
  // objekter should always start unfiltered. Shared by both views — "the
  // exact same tab selection", not two parallel sets of area/status/
  // search state.
  // Reads a starting area from ?area= if one was passed in (e.g. Historik's
  // "Åbn" links land here scoped to the relevant area) — one-time initial
  // value only, not kept in sync on every tab click.
  const { tenantId: routeTenantId } = useParams();
  const navigate = useNavigate();
  // ?policy=<name> (from Indstillinger): only devices where that policy is not fulfilled.
  const policyFilter = searchParams.get('policy');
  // &missing=1&platform=Windows: the devices of that platform that the policy does NOT reach.
  const missingFilter = searchParams.get('missing') === '1';
  const policyPlatform = searchParams.get('platform');
  // A deep link (?area=…) wins over what was remembered.
  const [stored] = useState(() => (searchParams.get('area') ? {} : readWqState(routeTenantId)));
  const [areaFilter, setAreaFilter] = useState(() => {
    const initial = searchParams.get('area');
    return AREA_KEYS.includes(initial) ? initial : 'alle';
  });
  const [statusFilter, setStatusFilter] = useState('alle');
  const [search, setSearch] = useState('');
  const detailBodyRef = useRef(null);
  // null = every category collapsed — the default, per feedback: nothing
  // opens until you click it.
  // ?finding= and ?item= (from Beslutninger & risici) open that finding and object directly.
  const [selectedKey, setSelectedKey] = useState(() => searchParams.get('finding') || stored.key || null);
  const [selectedItemId, setSelectedItemId] = useState(() => searchParams.get('item') || stored.item || null);
  useEffect(() => {
    try {
      sessionStorage.setItem(wqStateKey(routeTenantId), JSON.stringify({ area: areaFilter, key: selectedKey, item: selectedItemId }));
    } catch {
      // session storage unavailable — nothing is remembered
    }
  }, [routeTenantId, areaFilter, selectedKey, selectedItemId]);
  // A policy opened from this page's own Politikker/Medlemskab-tab — shown as an overlay right here,
  // never a navigation to Politikker, so whatever you had open in Arbejdskø is still open behind it.
  // ?device=<id> (from Konfiguration, Politikker) opens that device in Alle objekter: a stub until the real item is built.
  const deviceParam = searchParams.get('device');
  const [tabelSelectedObj, setTabelSelectedObj] = useState(() => (deviceParam ? { area: 'devices', id: deviceParam, stub: true } : null));
  const [tabelSort, setTabelSort] = useState({ key: 'name', dir: 'asc' });
  // Same fixed 560/460/760 as every other page with a detail panel (Compliance/Konfiguration/
  // Beslutninger) — per direct instruction, this should match everywhere rather than being its own
  // viewport-scaled, mode-dependent size. Alle objekter's table is the flex-based .tabel-row column
  // system now (not the old fixed-width grid that used to truncate at a narrower width), so it reflows
  // instead of truncating at the shared default.
  const detailPanel = useResizablePanel(600, 340, 1000);
  const itList = useItList(tenant?.tenantId);
  // Visning/Fokus line up with the right edge of the list's frame (measured, not a guessed offset), so it stays exact when the panel is dragged or the window resized.
  const pageRef = useRef(null);
  const [toolsRight, setToolsRight] = useState(null);
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const measure = () => {
      const head = page.querySelector('.head');
      const card = page.querySelector('.wq-menu__card');
      if (!head || !card) return;
      const v = Math.round(head.getBoundingClientRect().right - card.getBoundingClientRect().right);
      setToolsRight((prev) => (prev === v ? prev : v));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });
  // The PIM recommendation can be folded to its heading line; the choice is kept per tenant in this browser.
  const pimKey = 'nc-pim-rec-hidden-' + routeTenantId;
  const [pimFolded, setPimFolded] = useState(() => { try { return localStorage.getItem(pimKey) === '1'; } catch { return false; } });
  const togglePim = () => setPimFolded((v) => { const n = !v; try { localStorage.setItem(pimKey, n ? '1' : '0'); } catch { /* private mode */ } return n; });
  // Which Fund groups are visually expanded — independent of selectedKey (which group's items feed the
  // detail panel/picker) now that more than one can be open at once, per direct instruction.
  const [showAllKeys, setShowAllKeys] = useState(() => new Set());
  // Cards and priority sections the user has folded in. Everything is open by default.
  const [collapsedKeys, setCollapsedKeys] = usePersistentSet('nc-wq-collapsed');
  // Which cards of the side panel are pressed open; they fold back when another object is chosen.
  // What the user has opened or closed in the side panel is remembered by the card's title, across objects and pages.
  const { isOpen: isCardOpen, toggle: toggleCard, setAll: setCardsOpen } = usePanelOpen();
  // Fokus: while it is on, the categories you are not working in step back. It can be switched off for good (remembered
  // in this browser). Pressing anywhere that is not a category, the side panel or a window ends the focus.
  const [focusOn, toggleFocus] = useFocusMode();
    useEffect(() => {
    if (!focusOn || !selectedKey) return undefined;
    function onDown(e) {
      if (e.target.closest && e.target.closest('.wqc, .detail-panel-col, .wqm, .dp__pop, .op-pop, .wq-focus')) return;
      setSelectedKey(null);
      setSelectedItemId(null);
      setPicked(new Set());
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [focusOn, selectedKey]);
  const [collapsedTones, setCollapsedTones] = usePersistentSet('nc-wq-collapsed-tones');
  // Objects ticked for one shared decision.
  const [picked, setPicked] = useState(() => new Set());
  const [tabelShowAll, setTabelShowAll] = useState(false);
  const [notes, setNotes] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [decisionType, setDecisionType] = useState('');
  const [owner, setOwner] = useState({ name: '', email: '' });
  const [reviewDate, setReviewDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionScope, setDecisionScope] = useState('obj');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    listAuditNotes(tenant.tenantId).then((d) => !cancelled && Array.isArray(d.notes) && setNotes(d.notes)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  // A large tenant's full "Alle områder" inventory can run into the
  // thousands of rows — rendering them all unconditionally is what breaks
  // the page, not the search itself (search already narrows the list
  // before this cap ever applies). Re-collapse to the capped view whenever
  // the filters change so switching areas doesn't stay stuck "expanded".
  useEffect(() => {
    setTabelShowAll(false);
  }, [areaFilter, statusFilter, search, viewMode]);

  const { noteByFinding } = useMemo(() => {
    const byFinding = new Map();
    (notes || []).forEach((n) => {
      if (!n.itemKey || !n.findingKey) return;
      const key = `${n.itemKey}::${canonicalFindingKey(n.findingKey)}`;
      const prev = byFinding.get(key);
      if (!prev || new Date(n.createdAt) > new Date(prev.createdAt)) byFinding.set(key, n);
    });
    return { noteByFinding: byFinding };
  }, [notes]);
  // What still needs action in a finding: its objects, less those whose decision still holds.
  const settledIn = (g) => g.items.filter((i) => isSettledNote(noteByFinding.get(`${i.id}::${g.key}`))).length;
  const openIn = (g) => g.items.length - settledIn(g);
  const q = search.trim().toLowerCase();
  // "Brugere 17": findings per area, red when something needs action.
  const areaCounts = useMemo(() => {
    const c = {};
    nonEmptyGroups.forEach((g) => {
      const e = c[g.area] || (c[g.area] = { bad: 0, warn: 0 });
      if (g.tone === 'bad') e.bad += openIn(g);
      else if (g.tone === 'warn') e.warn += openIn(g);
    });
    return c;
  }, [nonEmptyGroups, noteByFinding]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalBad = AREA_KEYS.reduce((n, k) => n + (areaCounts[k]?.bad || 0), 0);
  const totalFindings = totalBad + AREA_KEYS.reduce((n, k) => n + (areaCounts[k]?.warn || 0), 0);
  const visibleGroups = nonEmptyGroups
    .filter((g) => areaFilter === 'alle' || g.area === areaFilter)
    .filter((g) => statusFilter === 'alle' || g.tone === statusFilter)
    .filter((g) => !q || g.title.toLowerCase().includes(q) || g.items.some((i) => (i.name || '').toLowerCase().includes(q)))
    .sort((a, b) => (a.tone !== b.tone ? (a.tone === 'bad' ? -1 : 1) : b.items.length - a.items.length));
  // The I orden filter: what passes, per check. Read-only: there is nothing to decide on something that is in order.
  const visibleOk = okGroups.filter((g) => (areaFilter === 'alle' || g.area === areaFilter) && (!q || g.title.toLowerCase().includes(q) || g.items.some((i) => (i.name || '').toLowerCase().includes(q))));
  // In Alle the green frame sits last and starts folded (it is opened with its chevron); under the I orden filter it is open.
  const [okOpenInAll, setOkOpenInAll] = useState(false);
  function renderOkFrame(inAll) {
    const folded = inAll ? !okOpenInAll : collapsedTones.has('ok');
    const toggle = inAll
      ? () => setOkOpenInAll((v) => !v)
      : () => setCollapsedTones((prev) => { const next = new Set(prev); if (next.has('ok')) next.delete('ok'); else next.add('ok'); return next; });
    return (
      <StatusFrame key="ok" tone="ok" title="I orden" count={visibleOk.reduce((n, g) => n + g.items.length, 0)} folded={folded} onToggle={toggle}>
        {visibleOk.map(renderOkGroup)}
      </StatusFrame>
    );
  }
  function renderOkGroup(g) {
    const all = showAllKeys.has(g.key);
    const rows = all ? g.items : g.items.slice(0, FUND_ROWS);
    return (
      <section key={g.key} className="wqc wqc--ok">
        <header className="wqc__h" title={g.hint} style={{ cursor: 'default' }}>
          <h3>{g.title}{areaFilter === 'alle' && <small>{areaLabel(g.area)}</small>}</h3>
          <span className="wqc__pill wqc__pill--ok">{g.items.length} af {g.total} i orden</span>
        </header>
        <div className="wqc__rows">
          {rows.map((item) => (
            <div
              key={item.id}
              className={`wqc__row${selectedKey === g.key && selectedItemId === item.id ? ' is-sel' : ''}`}
              onClick={() => { activateGroup(g.key); openItem(item.id); }}
            >
              <span className="wqc__ic wqc__ic--ok"><PortalIcon name="check" size={15} /></span>
              <span className="wqc__nm"><span className="wqc__l1"><b title={item.name}>{item.name}</b></span></span>
              {item.sub && <span className="wqc__meta wqc__meta--fund" title={item.sub}>{item.sub}</span>}
              <span className="wqc__tags" />
            </div>
          ))}
        </div>
        {g.items.length > rows.length && (
          <footer className="wqc__f"><span className="wqc__lft"><button type="button" className="wqc__more" onClick={() => setShowAllKeys((prev) => new Set(prev).add(g.key))}>Vis alle {g.items.length} →</button></span></footer>
        )}
      </section>
    );
  }
  // ?device=<id> (from Konfiguration, a policy's result): open that device in its finding. A device that is not part of any
  // finding says so, and points to Alle objekter.
  const handledDeviceParam = useRef(null);
  const [deviceNotFinding, setDeviceNotFinding] = useState(false);
  useEffect(() => {
    if (!deviceParam || viewMode !== 'fund' || !devices || handledDeviceParam.current === deviceParam) return;
    const g = nonEmptyGroups.find((x) => x.area === 'devices' && x.items.some((i) => i.id === deviceParam));
    handledDeviceParam.current = deviceParam;
    if (!g) { setDeviceNotFinding(true); return; }
    setAreaFilter('devices');
    setSelectedKey(g.key);
    setSelectedItemId(deviceParam);
    setShowAllKeys((prev) => new Set(prev).add(g.key));
    setCollapsedKeys((prev) => { const n = new Set(prev); n.delete(g.key); return n; });
    setCollapsedTones((prev) => { const n = new Set(prev); n.delete(g.tone === 'bad' || g.tone === 'warn' ? g.tone : 'other'); return n; });
  }, [deviceParam, viewMode, devices, nonEmptyGroups, setCollapsedKeys, setCollapsedTones]);
  // The priority a finding sorts under: red, orange, and everything else.
  const toneOf = (g) => (g.tone === 'bad' || g.tone === 'warn' ? g.tone : 'other');
  const allFundOpen = collapsedKeys.size === 0 && collapsedTones.size === 0;
  const toggleAllFund = () => {
    if (allFundOpen) {
      setCollapsedKeys(new Set(visibleGroups.map((g) => g.key)));
      setCollapsedTones(new Set(visibleGroups.map(toneOf)));
    } else {
      setCollapsedKeys(new Set());
      setCollapsedTones(new Set());
    }
  };
  const toggleIn = (setter, key) => setter((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });

  // Tabel's full population for the active area tab — every object
  // regardless of status, unlike the fund groups above (which only ever
  // hold non-compliant items). Reuses the exact same raw tenant data the
  // fund builders above already consume, so there's one source of truth
  // per area, not a second copy living inside a separately-mounted page.
  // "Alle områder" concatenates every area's population, each item tagged
  // with its real area — the same shared area tab Fund's own "Alle
  // områder" uses, not a second parallel concept.
  // meta mirrors each area's own real FlatObjectList columns (Enheder's
  // Styresystem/Krypteret, Identitet's Seneste login/MFA, etc.) — same
  // fields, same labels, so Tabel reads exactly like each area's own page
  // did, just sharing Arbejdskø's filters instead of mounting a copy of it.
  function itemsForArea(key) {
    switch (key) {
      case 'identity': {
        if (!mfa?.users) return [];
        const signInByUserId = {};
        (risks?.memberSignIns || []).forEach((u) => { signInByUserId[u.id] = u; });
        const fmtDays = (d) => (d == null ? 'Ikke tilgængeligt endnu' : d === 0 ? 'I dag' : `${d} dage siden`);
        const members = mfa.users.map((u) => {
          const signIn = signInByUserId[u.id];
          const days = signIn ? signIn.daysSinceSignIn : undefined;
          return {
            id: u.id, name: u.displayName || 'Ukendt', sub: 'Bruger i organisationen', area: 'identity',
            passwordAgeDays: signIn ? daysSince(signIn.lastPasswordChangeDateTime) : null,
            meta: [
              ['Seneste login', fmtDays(days)],
              ['Ekstra loginbeskyttelse', u.hasMfa ? 'Aktiveret' : 'Mangler'],
              ['Kontostatus', u.accountEnabled === false ? 'Deaktiveret' : 'Aktiv'],
              ['Email', u.userPrincipalName || u.mail || 'Ikke tilgængeligt endnu'],
              ['Licens', (licensesByUserId[u.id] || [])[0] || 'Ingen licens'],
            ],
            groupsList: groupsByUserId[u.id] || [],
            rolesList: (rolesByUserId[u.id] || []).map((r) => ({ ...r, noMfa: !u.hasMfa })),
          };
        });
        const guests = (risks?.guests || []).map((g) => ({
          id: g.id, name: g.displayName || 'Ukendt', sub: 'Gæst', area: 'identity',
          meta: [
            ['Seneste login', fmtDays(daysSince(g.signInActivity?.lastSignInDateTime))],
            ['Ekstra loginbeskyttelse', '—'],
            ['Kontostatus', 'Gæst'],
            ['Email', g.mail || g.userPrincipalName || 'Ikke tilgængeligt endnu'],
            ['Licens', (licensesByUserId[g.id] || [])[0] || 'Ingen licens'],
          ],
          groupsList: groupsByUserId[g.id] || [],
          rolesList: rolesByUserId[g.id] || [],
        }));
        return [...members, ...guests];
      }
      case 'devices':
        return (devices?.devices || []).map((d) => ({
          id: d.id, name: d.deviceName || 'Ukendt', sub: d.userDisplayName || '—', area: 'devices', operatingSystem: d.operatingSystem || null,
          azureADDeviceId: d.azureADDeviceId || null,
          meta: [
            ['Styresystem', d.operatingSystem || 'Ikke tilgængeligt endnu'],
            ['Compliance', d.complianceState === 'compliant' ? 'Compliant' : [...new Set((d.complianceReasons || []).map(settingText))].join(', ') || 'Ikke compliant'],
            ['Krypteret', d.isEncrypted == null ? 'Ikke tilgængeligt endnu' : d.isEncrypted ? 'Ja' : 'Nej'],
          ],
          // Graph's own pre-resolved per-device policy state — no group
          // membership correlation needed on our side (see
          // deviceCompliancePolicyStates/deviceConfigurationStates in
          // handleDevices).
          compliancePolicyStates: d.compliancePolicyStates ?? null,
          configurationStates: d.configurationStates ?? null,
          partnerReportedThreatState: d.partnerReportedThreatState ?? null,
        }));
      case 'exchange': {
        // Every external address a mailbox forwards to (a mailbox can have
        // more than one rule) — shown in the list and in the detail panel.
        const forwardsByMailbox = {};
        (insights?.forwardingRules || []).forEach((r) => {
          if (!r.isExternal || !r.forwardsTo) return;
          const k = (r.mailbox || '').toLowerCase();
          if (!forwardsByMailbox[k]) forwardsByMailbox[k] = [];
          forwardsByMailbox[k].push(r.forwardsTo);
        });
        const recipientTypes = insights?.recipientTypes || null;
        const mailboxDelegates = insights?.mailboxDelegates || null;
        return (membership?.users || []).map((u) => {
          const key = (u.userPrincipalName || '').toLowerCase();
          const recipient = recipientTypes ? recipientTypes[key] : null;
          const delegates = mailboxDelegates ? mailboxDelegates[key] : null;
          return {
            id: u.id, name: u.displayName || 'Ukendt', sub: u.mail || u.userPrincipalName || '—', area: 'exchange',
            meta: [
              ['Type', recipient ? (RECIPIENT_TYPE_LABEL[recipient.type] || recipient.type || 'Ukendt') : 'Ikke tilgængeligt endnu'],
              ['Videresendelse', forwardsByMailbox[key] ? `Til ${forwardsByMailbox[key][0]}${forwardsByMailbox[key].length > 1 ? ` +${forwardsByMailbox[key].length - 1}` : ''}` : 'Ingen videresendelse'],
            ],
            forwardsTo: forwardsByMailbox[key] || [],
            sendOnBehalfTo: recipient?.sendOnBehalfTo || [],
            fullAccess: delegates?.fullAccess || null,
            sendAs: delegates?.sendAs || null,
          };
        });
      }
      case 'applications':
        // Same exclusion Applications.jsx and this page's own Fund builder
        // (appGroups, above) already apply — servicePrincipals includes
        // every Microsoft first-party app the tenant has (400-600+, none of
        // it something a customer manages), and this list was missing that
        // filter, hence showing an inflated, meaningless object count.
        return (applications?.applications || []).filter((a) => !a.isMicrosoftApp).map((a) => ({
          id: a.id, name: a.displayName || 'N/A', sub: a.publisherName || '', area: 'applications',
          meta: [['Tilladelser', `${a.permissionCount ?? 0}`], ['Sidst brugt', a.lastSignInDateTime ? `${daysSince(a.lastSignInDateTime)} dage siden` : 'Ikke tilgængeligt endnu']],
          certificateStatus: a.certificateStatus || null,
          secretExpiry: a.secretExpiry || null,
          hasVerifiedPublisher: a.hasVerifiedPublisher,
          permissions: a.permissions || [],
          permissionCount: a.permissionCount ?? null,
          owners: a.owners ?? null,
          appMembers: a.members ?? null,
        }));
      case 'roles': {
        // Role-centric, not assignment-centric — per direct feedback, the
        // role itself is the object you click; who's actually assigned to
        // it is what you see once you're in the detail panel (Medlemmer,
        // below), not a separate row per person per role.
        const byRole = new Map();
        const mfaByUserId = new Map((mfa?.users || []).map((u) => [u.id, u.hasMfa]));
        const signInByPrincipal = new Map((risks?.memberSignIns || []).map((u) => [u.id, u]));
        (risks?.privilegedRoles || []).forEach((r) => {
          const roleName = r.role || 'Ukendt rolle';
          if (!byRole.has(roleName)) byRole.set(roleName, []);
          byRole.get(roleName).push({
            id: r.id || r.principalId,
            displayName: r.displayName || r.principalId || 'Ukendt',
            isGuest: !!r.isGuest,
            status: r.status === 'eligible' ? 'eligible' : 'permanent',
            // What is asked about a role holder: are they protected, and are they still around.
            userPrincipalName: [
              r.status === 'eligible' ? 'PIM-berettiget' : 'Permanent',
              r.principalType === 'App' ? 'App' : r.principalType === 'Bruger' && !r.isGuest && mfaByUserId.has(r.principalId || r.id) ? (mfaByUserId.get(r.principalId || r.id) ? 'MFA ok' : 'uden MFA') : null,
              signInByPrincipal.get(r.principalId || r.id)?.daysSinceSignIn != null ? `login ${signInByPrincipal.get(r.principalId || r.id).daysSinceSignIn === 0 ? 'i dag' : `${signInByPrincipal.get(r.principalId || r.id).daysSinceSignIn} dage siden`}` : null,
            ].filter(Boolean).join(' · '),
          });
        });
        return [...byRole.entries()].map(([roleName, members]) => {
          // Two explicit columns, not one combined "3 (2 permanente)" cell —
          // now that PIM-eligible data actually resolves (see the
          // CultureNotFoundException fix), the eligible count is real and
          // worth its own labeled column rather than being left implicit
          // (previously it wasn't shown at all in this table). Naming each
          // column for exactly what it counts avoids the earlier "two bare
          // numbers with no stated relationship" confusion — a labeled
          // "Permanent"/"PIM-berettiget" pair states its own relationship.
          const permanentCount = members.filter((m) => m.status === 'permanent').length;
          const eligibleCount = members.length - permanentCount;
          return {
            id: roleName, name: roleName, sub: '', area: 'roles',
            meta: [['Permanent', String(permanentCount)], ['PIM-berettiget', String(eligibleCount)]],
            members,
          };
        });
      }
      case 'conditional-access':
        return (ca?.policies || []).map((p) => {
          const controlsShort = p.controlsShort || [];
          const controlsLabel = controlsShort.length > 0 ? controlsShort.join(', ') : 'Intet konfigureret';
          return {
            id: p.id,
            name: displayCaPolicyName(p.displayName),
            // Short and structural instead of the full resolved-names
            // sentence (p.summary) — per direct feedback, that sentence
            // repeated under every name in the list AND in the detail
            // panel's "Hvorfor det er vigtigt" AND partly in Evidens.
            sub: controlsShort.length > 0 ? `Kræver ${controlsLabel}` : 'Ingen adgangskrav konfigureret',
            area: 'conditional-access',
            meta: [
              ['Status', p.state === 'disabled' ? 'Deaktiveret' : p.state === 'enabledForReportingButNotEnforced' ? 'Kun rapportering' : 'Aktiveret'],
              ['Gælder for', p.targetSummaryShort || 'Alle brugere'],
              ['Kræver', controlsLabel],
            ],
            controlsShort,
            excludeSummary: p.excludeSummaryShort || null,
            includeUsers: p.includeUsers || null,
            excludeUsers: p.excludeUsers || null,
            excludeUsersList: p.excludeUsersList || [],
            excludeAppsList: p.excludeAppsList || [],
            includeApps: p.includeApps || null,
            excludeApps: p.excludeApps || null,
            state: p.state,
            operator: p.grantControls?.operator || null,
            createdDateTime: p.createdDateTime || null,
            modifiedDateTime: p.modifiedDateTime || null,
            sessionControlsSummary: p.sessionControlsSummary || [],
            riskLevels: p.riskLevels || { signIn: [], user: [] },
            locationsInclude: p.locationsInclude || null,
            locationsExclude: p.locationsExclude || null,
            excludesTrustedLocation: !!p.excludesTrustedLocation,
            platformsInclude: p.conditions?.platforms || [],
            clientAppTypes: p.clientAppTypes || [],
            authStrength: p.authStrength || null,
            deviceFilter: p.deviceFilter || null,
            authFlows: p.authFlows || [],
            userActions: p.userActions || [],
            requiresAppProtection: !!p.requiresAppProtection,
          };
        });
      case 'security':
        if (!security) return [];
        return [
          ...(security.incidents || []).map((i) => ({
            id: i.id, name: i.displayName || 'Unavngivet incident', sub: 'Incident', area: 'security',
            // 'Sagsstatus' (the incident's own lifecycle state, e.g. "active"/
            // "resolved") — not labeled 'Status' since Tabel already has a
            // trailing Status column for the compliance tone, and two columns
            // both named "Status" was confusing (direct feedback).
            meta: [['Alvorlighed', i.severity || 'Ikke tilgængeligt endnu'], ['Sagsstatus', i.status || 'Ikke tilgængeligt endnu']],
            description: i.description || null,
            determination: i.determination || i.classification || null,
            assignedTo: i.assignedTo || null,
            lastUpdateDateTime: i.lastUpdateDateTime || null,
          })),
          ...(security.alerts || []).map((a) => ({
            id: a.id, name: a.title || 'Unavngivet alert', sub: 'Alert', area: 'security',
            meta: [['Alvorlighed', a.severity || 'Ikke tilgængeligt endnu'], ['Sagsstatus', a.status || 'Ikke tilgængeligt endnu']],
            description: a.description || null,
            determination: a.determination || null,
            assignedTo: a.assignedTo || null,
            lastUpdateDateTime: a.lastUpdateDateTime || null,
          })),
        ];
      case 'sharepoint':
        // One row per real-world workspace, not per Graph object — a
        // Microsoft 365 group's Team and SharePoint site used to each show
        // up as their own same-named row; the backend now correlates them
        // onto one `workspaces` entry (see handleGroups), so there's no
        // dedup-by-id filter needed here any more. Ejere lead the meta
        // columns ahead of Medlemmer/Gæster — per direct feedback, the
        // owner is normally who you'd actually contact about the object,
        // more useful at a glance than a raw member count.
        return [
          ...(sites || []).map((s) => ({
            id: s.id, name: s.displayName, sub: 'SharePoint-site', area: 'sharepoint',
            meta: [
              ['Type', 'SharePoint-site'],
              ['Seneste aktivitet', s.lastActivityDateTime ? `${daysSince(s.lastActivityDateTime)} dage siden` : 'Ikke tilgængeligt endnu'],
            ],
          })),
          ...(workspaces || []).map((w) => ({
            id: w.id, name: w.displayName, sub: (w.resources || []).join(' + ') || 'Microsoft 365-gruppe', area: 'sharepoint',
            meta: [
              ['Type', (w.resources || []).map((r) => (r === 'SharePoint Site' ? 'SharePoint-site' : r)).join(' + ') || 'Microsoft 365-gruppe'],
              ['Ejere', summarizeNames(w.owners?.map((o) => o.displayName)) || 'Ingen registreret'],
              ['Medlemmer', w.memberCount != null ? `${w.memberCount}` : 'Ikke tilgængeligt endnu'],
              ['Gæster', w.guestCount != null ? `${w.guestCount}` : 'Ikke tilgængeligt endnu'],
              ['Seneste aktivitet', w.lastActivityDateTime ? `${daysSince(w.lastActivityDateTime)} dage siden` : 'Ikke tilgængeligt endnu'],
            ],
            // Full member list (owners included, flagged isOwner) — the
            // detail panel's Overblik tab splits Ejere from Medlemmer.
            members: w.members || [],
            visibility: w.visibility || null,
            lifecyclePolicy,
          })),
          ...(groups || []).map((g) => ({
            id: g.id, name: g.displayName, sub: g.type, area: 'sharepoint',
            meta: [
              ['Type', g.type || 'Gruppe'],
              ['Medlemmer', g.memberCount != null ? `${g.memberCount}` : 'Ikke tilgængeligt endnu'],
              ['Gæster', g.guestCount != null ? `${g.guestCount}` : 'Ikke tilgængeligt endnu'],
            ],
            members: g.members || [],
          })),
        ];
      case 'licenses':
        return (licenses || []).map((l) => ({
          id: l.skuId, name: l.name, sub: 'Licens', area: 'licenses',
          meta: [['Tildelt', `${l.assigned}`], ['Købt', `${l.total}`], ['Ledige', `${Math.max(l.total - l.assigned, 0)}`]],
          users: l.users || [],
          servicePlans: l.servicePlans || [],
        }));
      default:
        return [];
    }
  }
  const tabelItems = useMemo(() => {
    return areaFilter === 'alle' ? AREA_KEYS.flatMap(itemsForArea) : itemsForArea(areaFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaFilter, mfa, risks, devices, membership, applications, ca, security, sites, workspaces, groups, licenses, licensesByUserId]);

  useEffect(() => {
    if (!tabelSelectedObj?.stub) return;
    const found = tabelItems.find((i) => i.area === 'devices' && i.id === tabelSelectedObj.id);
    if (found) setTabelSelectedObj(found);
  }, [tabelItems, tabelSelectedObj]);

  // A Tabel object that's currently flagged already lives inside a real
  // fund group (allGroups) — reusing that group gives the exact same
  // recommend/whyExplained text Fund would show for it. A compliant
  // object belongs to no group, so a minimal stand-in is built for it
  // instead, just enough for the shared detail panel to render an "OK"
  // state. Keyed by area+id (not id alone) since ids aren't guaranteed
  // unique across different areas' object types.
  const tabelToneMap = useMemo(() => {
    const m = new Map();
    allGroups.forEach((g) => g.items.forEach((i) => {
      const k = `${g.area}:${i.id}`;
      if (!m.has(k)) m.set(k, g);
    }));
    return m;
  }, [allGroups]);
  const TABEL_STATUS_LABEL = { bad: 'Kræver handling', warn: 'Bør gennemgås', ok: 'I orden' };
  function tabelToneFor(item) {
    return tabelToneMap.get(`${item.area}:${item.id}`)?.tone || 'ok';
  }

  // Fixed per-area column set (TABEL_COLUMNS) — no longer derived from
  // whatever labels happen to appear in the data, so the header can't
  // silently swap based on sort order or which item type loads first.
  const tabelMetaColumnsFixed = useMemo(() => {
    if (areaFilter === 'alle') return [];
    return TABEL_COLUMNS[areaFilter] || [];
  }, [areaFilter]);
  // The columns can be moved by dragging a header (or Alt + arrow keys on a focused header). Name stays first and Status last.
  // The order is kept per area in this browser.
  const [colOrder, setColOrder] = useState(() => { try { return JSON.parse(localStorage.getItem('nc-tabel-cols') || '{}') || {}; } catch { return {}; } });
  const { flexOf, startResize, reset: resetWidth } = useColumnWidths('nc-tabel-widths', areaFilter);
  const colFlex = (key) => flexOf(key, key === 'name' ? '1.6 1 0%' : '1 1 0%');
  const [dragCol, setDragCol] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const tabelMetaColumns = useMemo(() => {
    const saved = Array.isArray(colOrder[areaFilter]) ? colOrder[areaFilter] : [];
    const known = saved.filter((label) => tabelMetaColumnsFixed.includes(label));
    return [...known, ...tabelMetaColumnsFixed.filter((label) => !known.includes(label))];
  }, [colOrder, areaFilter, tabelMetaColumnsFixed]);
  function moveColumn(label, beforeLabel) {
    if (!label || label === beforeLabel) return;
    const next = tabelMetaColumns.filter((l) => l !== label);
    const at = beforeLabel ? next.indexOf(beforeLabel) : -1;
    next.splice(at < 0 ? next.length : at, 0, label);
    setColOrder((prev) => {
      const all = { ...prev, [areaFilter]: next };
      try { localStorage.setItem('nc-tabel-cols', JSON.stringify(all)); } catch { /* not remembered */ }
      return all;
    });
  }
  function nudgeColumn(label, step) {
    const i = tabelMetaColumns.indexOf(label);
    const j = i + step;
    if (i < 0 || j < 0 || j >= tabelMetaColumns.length) return;
    moveColumn(label, step < 0 ? tabelMetaColumns[j] : tabelMetaColumns[j + 1] || null);
  }

  // Same column shape FlatObjectList's export buttons use ({key, label,
  // value: fn}) — built from Alle objekter's own columns (Navn/Område or
  // Navn/<area's meta labels>, plus Status) rather than a shared definition,
  // since Tabel's columns already vary by areaFilter.
  const tabelExportColumns = useMemo(() => {
    const cols = [{ key: 'name', label: 'Navn', value: (item) => item.name || '' }];
    if (areaFilter === 'alle') {
      cols.push({ key: 'area', label: 'Område', value: (item) => areaTabLabel(item.area) });
    } else {
      tabelMetaColumns.forEach((label) => {
        cols.push({ key: `meta:${label}`, label, value: (item) => item.meta?.find(([l]) => l === label)?.[1] ?? '—' });
      });
    }
    cols.push({ key: 'status', label: 'Status', value: (item) => TABEL_STATUS_LABEL[tabelToneFor(item)] });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaFilter, tabelMetaColumns]);
  const [tabelExporting, setTabelExporting] = useState(false);
  const [tabelExportMenuOpen, setTabelExportMenuOpen] = useState(false);
  const tabelExportMenuRef = useRef(null);
  useEffect(() => {
    function onClick(e) {
      if (tabelExportMenuRef.current && !tabelExportMenuRef.current.contains(e.target)) setTabelExportMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  async function handleTabelExportPdf() {
    setTabelExportMenuOpen(false);
    setTabelExporting(true);
    try {
      await exportToPdf('Alle objekter', tabelExportColumns, tabelFilteredItems);
    } finally {
      setTabelExporting(false);
    }
  }
  async function handleTabelExportExcel() {
    setTabelExportMenuOpen(false);
    setTabelExporting(true);
    try {
      await exportToExcel('Alle objekter', tabelExportColumns, tabelFilteredItems);
    } finally {
      setTabelExporting(false);
    }
  }

  const tabelFilteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? tabelItems.filter((i) =>
          (i.name || '').toLowerCase().includes(q) ||
          (i.sub || '').toLowerCase().includes(q) ||
          (i.meta || []).some(([, v]) => String(v ?? '').toLowerCase().includes(q))
        )
      : tabelItems;
    if (statusFilter !== 'alle') list = list.filter((i) => tabelToneFor(i) === statusFilter);
    if (policyFilter) {
      const statesOf = (i) => [...(i.compliancePolicyStates || []), ...(i.configurationStates || [])];
      const notOk = (s) => s.displayName === policyFilter && s.state && !['compliant', 'notApplicable', 'unknown', 'pending'].includes(s.state);
      const osMatches = (os) => {
        const t = String(os || '').toLowerCase();
        if (policyPlatform === 'iOS') return t === 'ios' || t === 'ipados';
        if (policyPlatform === 'macOS') return t === 'macos';
        return !policyPlatform || t.startsWith(policyPlatform.toLowerCase());
      };
      // A device whose policy states could not be read is left out: nothing is claimed about it.
      const isMissing = (i) => i.compliancePolicyStates && i.configurationStates && osMatches(i.operatingSystem) && !statesOf(i).some((s) => s.displayName === policyFilter);
      list = list.filter((i) => i.area === 'devices' && (missingFilter ? isMissing(i) : statesOf(i).some(notOk)));
    }
    const dir = tabelSort.dir === 'asc' ? 1 : -1;
    const metaLabel = tabelSort.key.startsWith('meta:') ? tabelSort.key.slice(5) : null;
    return [...list].sort((a, b) => {
      if (tabelSort.key === 'area') return dir * areaTabLabel(a.area).localeCompare(areaTabLabel(b.area), 'da');
      if (tabelSort.key === 'status') return dir * TABEL_STATUS_LABEL[tabelToneFor(a)].localeCompare(TABEL_STATUS_LABEL[tabelToneFor(b)], 'da');
      if (metaLabel !== null) {
        const av = a.meta?.find(([l]) => l === metaLabel)?.[1];
        const bv = b.meta?.find(([l]) => l === metaLabel)?.[1];
        // Plain numbers (member counts, license counts, ...) sorted as
        // strings put "17" before "9" — compare numerically whenever both
        // sides are actually numeric, fall back to text otherwise (dates
        // like "5 dage siden", statuses, etc.).
        const an = av === '' || av == null ? NaN : Number(av);
        const bn = bv === '' || bv == null ? NaN : Number(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return dir * (an - bn);
        return dir * String(av ?? '').localeCompare(String(bv ?? ''), 'da');
      }
      return dir * (a.name || '').localeCompare(b.name || '', 'da');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabelItems, search, statusFilter, tabelSort, tabelToneMap, policyFilter, missingFilter, policyPlatform]);

  const tabelSelectedItemRaw = tabelSelectedObj && tabelFilteredItems.some((i) => i.area === tabelSelectedObj.area && i.id === tabelSelectedObj.id)
    ? tabelSelectedObj
    : tabelFilteredItems[0] || null;
  const tabelSelectedGroup = tabelSelectedItemRaw
    ? tabelToneMap.get(`${tabelSelectedItemRaw.area}:${tabelSelectedItemRaw.id}`) || {
        key: `tabel-ok-${tabelSelectedItemRaw.area}-${tabelSelectedItemRaw.id}`, tone: 'ok', area: tabelSelectedItemRaw.area, title: 'Compliant',
        hint: 'Lever op til de nuværende krav.', recommend: '',
        items: [tabelSelectedItemRaw],
      }
    : null;

  const selected = viewMode === 'fund'
    ? (selectedKey ? visibleGroups.find((g) => g.key === selectedKey) || visibleOk.find((g) => g.key === selectedKey) || null : null)
    : tabelSelectedGroup;
  // Latest note per object, scoped to the specific finding it was
  // documented under (findingKey). An object can appear in more than one
  // finding at once, and a decision is about one specific risk — so it only
  // counts on the finding it was written on. A note with no findingKey (the
  // object's own detail page, older notes) is not a decision on any finding
  // here; it still shows under "Tidligere noter" on the object and in
  // Beslutninger & risici.
  function findNote(itemId, findingKey) {
    return (findingKey && noteByFinding.get(`${itemId}::${findingKey}`)) || null;
  }
  // The decision on an object as one line for the IT mail: its type, then the reason in quotation marks. Empty when nothing is decided.
  function decisionNoteOf(itemId, findingKey) {
    const n = findNote(itemId, findingKey);
    if (!n || !n.decisionType) return '';
    const label = (DECISION_TYPES.find(([key]) => key === n.decisionType) || [])[1] || 'Beslutning';
    return `${label}.${n.text ? ` \u201C${n.text}\u201D` : ''}`;
  }
  function statusBadge(itemId, findingKey) {
    return getStatusBadge(findNote(itemId, findingKey));
  }
  // How much of the chosen area is documented: the "0/16" on each group, added up (Alle områder = the whole tenant),
  // plus the date of the latest decision.
  // Documented objects stay visible by default now — per direct feedback,
  // hiding them read as things disappearing rather than being resolved.
  // statusBadge (above) is how a documented object's actual decision still
  // shows on the row.
  // Objects that already have a decision go last (and are greyed out), so
  // what is still open is what you see first.
  const visibleItems = selected
    ? [...selected.items].sort((a, b) => (isSettledNote(findNote(a.id, selected.key)) ? 1 : 0) - (isSettledNote(findNote(b.id, selected.key)) ? 1 : 0))
    : [];
  // Once a category is open, defaults to its first (visible) affected
  // object rather than staying empty — the approved mockup's panel always
  // shows a specific object's card, never an intermediate "pick one" screen.
  const selectedItem = viewMode === 'fund'
    ? (selected ? selected.items.find((i) => i.id === selectedItemId) || visibleItems[0] || selected.items[0] || null : null)
    : tabelSelectedItemRaw;
  // Fund's own items never carry the real per-type fields (name/sub/
  // navPath only) — Tabel's full inventory already computed them for the
  // very same object, so look them up there instead of duplicating the
  // extraction logic a second time.
  const selectedTabelItem = selectedItem
    ? tabelItems.find((i) => i.area === selected?.area && i.id === (selectedItem.lookupId || selectedItem.id)) || null
    : null;
  // The facts are the same in both views: everything Alle objekter shows for the object, plus whatever the finding adds
  // that Alle objekter does not (a Fund item's own list is shorter: no e-mail, no licence).
  const selectedMeta = useMemo(() => {
    if (!selectedItem) return null;
    const full = selectedTabelItem?.meta;
    const own = selectedItem.meta;
    if (!full) return own || null;
    if (!own) return full;
    // A fact Alle objekter could not fill in ("Ikke tilgængeligt endnu") is replaced by the object's own, when that has it.
    const gap = (v) => v == null || v === NOT_AVAILABLE;
    const ownByKey = new Map(own.map(([k, v]) => [k, v]));
    const merged = full.map(([k, v, ...rest]) => (gap(v) && ownByKey.has(k) && !gap(ownByKey.get(k)) ? [k, ownByKey.get(k), ...rest] : [k, v, ...rest]));
    return [...merged, ...own.filter(([k]) => !full.some(([x]) => x === k))];
  }, [selectedItem, selectedTabelItem]);
  // The same full record, merged over the Fund item — without this, every
  // per-type field read off the item itself (Defender-status, Secret nøgle,
  // Certifikatstatus, Lifecycle, policy states...) was always empty in
  // Fund view, since Fund items only carry name/sub/navPath.
  const selectedFullItem = useMemo(
    () => (selectedItem && selectedTabelItem ? { ...selectedItem, ...selectedTabelItem } : selectedItem),
    [selectedItem, selectedTabelItem]
  );
  // Fixed 4-tab shape for every area now (Dokumentation/Overblik/[navngivet
  // tredje faneblad]/Evidens) — per direct feedback, the structure stays
  // the same everywhere, only the data inside it varies. Owners/members are
  // re-derived from selectedItem inside the memo (not passed in as deps)
  // since they're new array literals every render otherwise.
  const detailSections = useMemo(() => {
    if (!selected || !selectedItem) return null;
    // From the full record, not the Fund item: a Fund item only carries name/sub/meta, so reading the
    // member list off it left the Medlemmer tab empty (no owners, members or guests) in Fund view.
    const owners = selectedFullItem?.members?.filter((m) => m.isOwner) || [];
    const nonOwnerMembers = selectedFullItem?.members?.filter((m) => !m.isOwner) || [];
    return buildDetailSections(selected.area, selectedMeta, selectedFullItem, owners, nonOwnerMembers, policyCtx, { rolesByUserId });
  }, [selected, selectedItem, selectedMeta, selectedFullItem, policyCtx, rolesByUserId]);

  // The same three tabs on every panel (Overblik · Evidens · Dokumentation). What used to be the area's own third
  // tab (Medlemskab, Politikker, Adgang, ...) now sits in Overblik, under the three descriptions.
  // The object's own tab only exists when the area has one for this object; otherwise fall back to Overblik.
  // A plain list of facts (Hændelsesdetaljer, Adgang for a domain ...) is the same facts as Evidens, so it has no tab of its own
  // in any area: it is one list under Evidens. Only real lists (members, access, permissions, rule, policies) get a tab.
  const mergedTab3 = !!(detailSections?.tab3 && !SPECIAL_TAB3.includes(detailSections.tab3.kind));


  // A row click (or its checkbox) inside a group that ISN'T the currently-active one first switches
  // the active group to it — picks/notes always target one group at a time, even with several open for
  // browsing at once.
  function activateGroup(key) {
    if (selectedKey === key) return;
    setSelectedKey(key);
    setPicked(new Set());
    setSelectedItemId(null);
  }

  function openItem(id) {
    setSelectedItemId(id);
    setNoteText('');
    setDecisionType('');
    setOwner({ name: '', email: '' });
    setReviewDate('');
    // detailTab is deliberately NOT reset here — all 3 tabs (Dokumentation/Detaljer/tab3) exist for
    // every object, so whichever one you're on (e.g. "Medlemskab og politikker") stays selected as
    // you click through other users/devices, instead of snapping back to Dokumentation each time.
    if (detailBodyRef.current) detailBodyRef.current.scrollTop = 0;
  }

  async function saveDecision() {
    if (!selected || !tenant || !decisionType) return;
    const targets = decisionScope === 'grp' && selected.items.length > 1
      ? selected.items
      : picked.size > 0 ? selected.items.filter((i) => picked.has(i.id)) : (selectedItem ? [selectedItem] : []);
    if (targets.length === 0) return;
    // The reason is optional in the window; the note itself must have text, so it falls back to the decision's name.
    const label = (DECISION_TYPES.find(([key]) => key === decisionType) || [])[1] || 'Beslutning';
    setSaving(true);
    setSaveError('');
    try {
      const prefix = PAGE_LABEL_PREFIX[selected.area] || 'Objekt';
      let result = { success: true };
      // The same decision is saved as its own record on each object.
      for (const target of targets) {
        // eslint-disable-next-line no-await-in-loop
        result = await createAuditNote(tenant.tenantId, selected.area, `${prefix}: ${target.rawName || target.name}`, noteText.trim() || label, {
          itemKey: target.id,
          findingKey: selected.key,
          decisionType,
          owner: owner.name.trim(),
          ownerEmail: owner.email,
          reviewDate,
          accepted: decisionType === 'accepted',
        });
        if (!result.success) break;
      }
      const d = await listAuditNotes(tenant.tenantId);
      if (Array.isArray(d.notes)) setNotes(d.notes);
      if (result.success) {
        setPicked(new Set());
        setNoteText('');
        setDecisionType('');
        setOwner({ name: '', email: '' });
        setReviewDate('');
        setDecisionOpen(false);
      } else {
        setSaveError('Kunne ikke gemme alle beslutninger. Prøv igen.');
      }
    } catch {
      setSaveError('Kunne ikke gemme. Prøv igen.');
    } finally {
      setSaving(false);
    }
  }


  // Like Overblik: the counts are not shown until every source the findings are built from has answered, so they cannot jump.
  const findingsReady = !risksLoading && !insightsLoading && !licensesLoading && !sitesLoading && !groupsLoading;
  if (loading || !findingsReady) return <LoadingState syncNote />;
  if (!tenant) return <p className="state-error">Data ikke tilgængelig</p>;

  // Ticked objects are documented together in the detail panel.
  const multiMode = picked.size > 0 && !!selected;
  // Without Entra ID P2 / Governance there is no PIM: administrator roles are permanent, and that is worth a recommendation.
  const pimMissing = areaFilter === 'roles' && /needs to have|P2|Governance license/i.test(risks?.privilegedRolesEligibilityWarning || '');
  const pickedItems = multiMode ? selected.items.filter((i) => picked.has(i.id)) : [];

  // The decision form — the same one for a single object and for several ticked ones.
  // One finding as a card of its own: the title and how much of it still needs a decision, up to FUND_ROWS of the
  // affected objects, and (only on the card that is selected) the green "Træf beslutning" button.
  // The hint/recommend texts and "Seneste login" come from useFindingGroups — nothing here is invented.
  function renderFundRow(g) {
    const isActiveGroup = selectedKey === g.key;
    const listed = g.items.filter((item) => !isSettledNote(findNote(item.id, g.key)));
    const total = listed.length;
    const open = total;
    // Objects that already have a decision go last, so what is still open is what you see first.
    const groupVisibleItems = listed;
    const groupShowAll = showAllKeys.has(g.key);
    const groupDisplayItems = groupShowAll ? groupVisibleItems : groupVisibleItems.slice(0, FUND_ROWS);
    const hidden = groupVisibleItems.length - groupDisplayItems.length;
    const pill = { tone: g.tone === 'bad' || g.tone === 'warn' ? g.tone : 'neutral', text: `${open} ${g.tone === 'bad' ? 'kræver handling' : g.tone === 'warn' ? 'bør gennemgås' : 'til gennemsyn'}` };
    const activeId = isActiveGroup ? (selectedItemId || groupVisibleItems[0]?.id) : null;
    const folded = collapsedKeys.has(g.key);
    return (
      <section key={g.key} className={`wqc wqc--${toneOf(g)}${isActiveGroup ? ' is-sel' : ''}${open === 0 ? ' is-done' : ''}${folded ? ' is-coll' : ''}`} onClick={() => activateGroup(g.key)}>
        <header className="wqc__h" title={g.hint} onClick={(e) => { e.stopPropagation(); activateGroup(g.key); toggleIn(setCollapsedKeys, g.key); }}>
          <h3>{g.title}{areaFilter === 'alle' && <small>{areaLabel(g.area)}</small>}</h3>
          <span className={`wqc__pill wqc__pill--${pill.tone}`}>{pill.text}</span>
          <button type="button" className="wqc__chev" aria-label={folded ? 'Åbn' : 'Luk'} aria-expanded={!folded} onClick={(e) => { e.stopPropagation(); toggleIn(setCollapsedKeys, g.key); }}><svg className="wqc__cv" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg></button>
        </header>
        {!folded && (
        <>
        <div className="wqc__rows">
          {groupDisplayItems.map((item) => {
            const isPicked = isActiveGroup && picked.has(item.id);
            const isCurrent = isActiveGroup && (picked.size > 0 ? isPicked : item.id === activeId);
            const note = findNote(item.id, g.key);
            const badge = statusBadge(item.id, g.key);
            const login = item.meta?.find(([k]) => k === 'Seneste login')?.[1];
            const base = item.why || item.reasonText || (login && login !== 'Ikke tilgængeligt endnu' ? `Sidst logget ind ${String(login).toLowerCase()}` : null) || item.sub || null;
            const line = base;
            return (
              <div
                key={item.id}
                className={`wqc__row${item.platformLabel ? ' wqc__row--dev' : ''}${isCurrent ? ' is-sel' : ''}${isSettledNote(note) ? ' is-done' : ''}`}
                onClick={(e) => { e.stopPropagation(); activateGroup(g.key); setPicked(new Set()); openItem(item.id); }}
              >
                <label className="wqc__pick" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isPicked}
                    onChange={() => {
                      if (!isActiveGroup) { activateGroup(g.key); setPicked(new Set([item.id])); return; }
                      setPicked((prev) => { const next = new Set(prev); if (next.has(item.id)) next.delete(item.id); else next.add(item.id); return next; });
                    }}
                    aria-label={`Vælg ${item.name}`}
                  />
                  <span className="wqc__cb" aria-hidden="true"><PortalIcon name="check" size={11} /></span>
                </label>
                <span className={`wqc__ic wqc__ic--${g.tone}`}><NcIcon name={item.platform || AREA_ICON[g.area] || 'grid'} size={15} /></span>
                <span className="wqc__nm">
                  <span className="wqc__l1"><b title={item.name}>{item.name}</b></span>
                </span>
                {item.platformLabel && <span className="wqc__plat">{item.platformLabel}</span>}
                {line && <span className="wqc__meta wqc__meta--fund" title={line}>{line}</span>}
                <span className="wqc__tags">
                  {itList.has(g.key, item.id) && (
                    <span className="itl-mark" title="På IT-opgaver">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>IT
                    </span>
                  )}
                  {note && <DecisionTag badge={badge} />}
                </span>
              </div>
            );
          })}
          {groupVisibleItems.length === 0 && <p className="fund-row__all-documented">Alle objekter i denne kategori er dokumenteret.</p>}
        </div>
        {(
          <footer className="wqc__f">
            <span className="wqc__lft">
              {(() => {
                const allPicked = isActiveGroup && picked.size === g.items.length && g.items.length > 0;
                const tip = allPicked ? 'Fjern markering' : `Markér alle ${total}`;
                return (
                  <button
                    type="button"
                    className={`wqc__ma${allPicked ? ' is-on' : ''}`}
                    data-tip={tip}
                    aria-label={tip}
                    aria-pressed={allPicked}
                    onClick={(e) => {
                      e.stopPropagation();
                      activateGroup(g.key);
                      setPicked(allPicked ? new Set() : new Set(g.items.map((it) => it.id)));
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 12l3 3 5-6" /></svg>
                  </button>
                );
              })()}
              {hidden > 0 && <button type="button" className="wqc__more" onClick={(e) => { e.stopPropagation(); setShowAllKeys((prev) => new Set(prev).add(g.key)); }}>Vis alle {groupVisibleItems.length} →</button>}
            </span>
            {isActiveGroup && (
              <span className="wqc__acts">
                {(() => {
                  const targets = g.items.filter((it) => (picked.size > 0 ? picked.has(it.id) : it.id === activeId));
                  const onList = targets.length > 0 && targets.every((it) => itList.has(g.key, it.id));
                  const toggleList = (e) => {
                    e.stopPropagation();
                    const objects = targets.map((it) => ({ id: it.id, name: it.name, note: decisionNoteOf(it.id, g.key) }));
                    (onList
                      ? itList.remove(g.key, objects.map((o) => o.id))
                      : itList.add({ key: g.key, title: g.title, area: areaLabel(g.area), tone: g.tone === 'bad' ? 'bad' : 'warn', objects })
                    ).catch(() => {});
                  };
                  return (
                    <button
                      type="button"
                      className={`itl-btn${onList ? ' is-on' : ''}`}
                      title={onList ? 'På IT-opgaver. Klik for at fjerne' : 'Tilføj til IT-opgaver'}
                      aria-pressed={onList}
                      disabled={targets.length === 0}
                      onClick={toggleList}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{onList ? <path d="M5 12l5 5L20 7" /> : <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />}</svg>
                      {onList ? 'På IT-opgaver' : `Tilføj ${targets.length > 1 ? `${targets.length} ` : ''}til IT-opgaver`}
                    </button>
                  );
                })()}
                <button type="button" className="wqc__btn" onClick={(e) => { e.stopPropagation(); activateGroup(g.key); openDecision(); }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 3h6v4H9z" /><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 12h6M9 16h6" /></svg>
                  {open === 0 ? 'Ændr beslutning' : 'Træf beslutning'}{picked.size > 1 ? ` (${picked.size})` : ''}
                </button>
              </span>
            )}
          </footer>
        )}
        </>
        )}
      </section>
    );
  }

  // The decision window. "Objekt" is the object(s) picked in the card (the selected one when none are ticked);
  // "Gruppe" is every object in the finding. Saved as one note per object, the same notes as before.
  function openDecision() {
    setDecisionScope('obj');
    setNoteText('');
    setDecisionType('');
    setOwner({ name: '', email: '' });
    setReviewDate('');
    setSaveError('');
    setDecisionOpen(true);
  }
  function closeDecision() {
    if (!saving) setDecisionOpen(false);
  }
  function renderDecisionModal() {
    if (!decisionOpen || !selected) return null;
    const objTargets = picked.size > 0 ? selected.items.filter((i) => picked.has(i.id)) : (selectedItem ? [selectedItem] : []);
    const canGroup = selected.items.length > 1;
    const scope = canGroup ? decisionScope : 'obj';
    const names = objTargets.map((i) => i.name);
    const count = scope === 'grp' ? selected.items.length : objTargets.length;
    return createPortal(
      <div className="wqm" onMouseDown={(e) => { if (e.target === e.currentTarget) closeDecision(); }}>
        <div className="wqm__box" role="dialog" aria-modal="true" aria-label="Træf beslutning">
          <button type="button" className="wqm__x" aria-label="Luk" onClick={closeDecision} disabled={saving}>×</button>
          <div className="wqm__head">
            <span className="wqm__badge" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v4H9z" /><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 12h6M9 16h6" /></svg>
            </span>
            <div>
              <h2>{selected.title}</h2>
              <p>{canGroup ? 'Beslutningen kan tages på to niveauer. Vælg det niveau, den gælder.' : 'Beslutningen gælder dette objekt.'}</p>
            </div>
          </div>
          <div className="wqm__sec">
            {canGroup && (
              <>
                <h4>Beslutningen gælder</h4>
                <div className="wqm__seg">
                  <button type="button" className={scope === 'obj' ? 'on' : ''} onClick={() => setDecisionScope('obj')}>
                    {objTargets.length > 1 ? 'Valgte objekter' : 'Objekt'}<small>{objTargets.length > 1 ? `${objTargets.length} objekter` : names[0]}</small>
                  </button>
                  <button type="button" className={scope === 'grp' ? 'on' : ''} onClick={() => setDecisionScope('grp')}>
                    Gruppe<small>hele gruppen · {selected.items.length} objekter</small>
                  </button>
                </div>
              </>
            )}
            <p className="wqm__info">
              Beslutningen gælder <b>{scope === 'grp' ? 'hele gruppen' : names.join(', ')}</b> · {count} {count === 1 ? 'objekt' : 'objekter'}
            </p>
          </div>
          <div className="wqm__sec">
            <h4>Beslutning</h4>
            <div className="decision-type-grid">
              {DECISION_TYPES.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  title={DECISION_HINT[key]}
                  className={`decision-type-btn decision-type-btn--${key}${decisionType === key ? ' is-selected' : ''}`}
                  onClick={() => setDecisionType(decisionType === key ? '' : key)}
                  disabled={saving}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="wqm__hint">{decisionType ? DECISION_HINT[decisionType] : 'Vælg en beslutning for at kunne gemme.'}</p>
          </div>
          <div className="wqm__sec">
            <textarea
              className="wqm__why"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Begrundelse (valgfri)"
              aria-label="Begrundelse"
              rows={2}
              disabled={saving}
            />
          </div>
          <div className="wqm__row">
            <OwnerPicker value={owner} members={ownerMembers} disabled={saving} onChange={setOwner} />
            <DatePicker value={reviewDate} onChange={setReviewDate} placeholder={decisionType === 'temp' ? 'Frist for rettelse' : 'Frist'} />
          </div>
          <div className="wqm__f">
            <span className="wqm__st">{saveError || (decisionType ? `Valgt: ${(DECISION_TYPES.find(([key]) => key === decisionType) || [])[1]}` : 'Ingen beslutning taget endnu')}</span>
            <div className="wqm__btns">
              <button type="button" className="wqm__luk" onClick={closeDecision} disabled={saving}>Luk</button>
              <button type="button" className="ok" onClick={saveDecision} disabled={saving || !decisionType}>{saving ? 'Gemmer…' : '✓ Beslut'}</button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }
  // The side panel for one object, as drawn: header, three tiles, the red "Hvis intet gøres" box, folded cards that
  // wake up when pressed, and the decision pinned at the bottom. Only what we really have is shown.
  function renderPanel() {
    const overblik = detailSections?.overblik || [];
    const evidens = detailSections?.evidens || [];
    const cleanup = detailSections?.cleanup;
    // One list of facts; a label that appears in both lists is shown once.
    const seen = new Set();
    const metaVal = (key) => { const v = (selectedMeta || []).find(([k]) => k === key)?.[1]; return v && v !== NOT_AVAILABLE ? v : null; };
    const baseFacts = [...(mergedTab3 ? detailSections.tab3.rows : []), ...overblik, ...evidens].filter(([k]) => (seen.has(k) ? false : (seen.add(k), true)));
    // What the three grey tiles used to say (whose device it is, last sign-in or sync) now sits in Evidens, once.
    const extraFacts = [];
    const ownerName = selectedItem.sub && selectedItem.sub !== '—' ? selectedItem.sub : null;
    if (selected.area === 'devices' && ownerName && !seen.has('Tilhører')) extraFacts.push(['Tilhører', ownerName]);
    ['Seneste login', 'Sidst synkroniseret'].forEach((k) => { const v = metaVal(k); if (v && !seen.has(k)) extraFacts.push([k, v]); });
    const factRows = kvRows([...extraFacts, ...baseFacts]);
    const [toneText, toneKey] = { bad: ['Kræver handling', 'r'], warn: ['Bør gennemgås', 'o'], ok: ['I orden', 'g'] }[selected.tone] || ['Til gennemsyn', 'n'];
    // Something that is in order: no warning, no recommendation and no way to fix it, only why it is fine and its facts.
    const isOk = selected.tone === 'ok';
    // One line in the box: what is wrong with this object (the explanation of the whole group is under Hvad er det).
    const warnings = isOk ? [] : [selectedItem.why || selected.hint].filter(Boolean);
    const risk = isOk ? null : FINDING_RISK[selected.key] || (warnings.length ? null : 'Ingen handling nødvendig.');
    // The recommendation of the finding; with no warning and no recommendation the one line says nothing needs doing.
    const advice = isOk ? null : selected.recommend || (warnings.length ? null : risk);
    const note = findNote(selectedItem.id, selected.key);
    const decided = note?.decisionType ? (DECISION_TYPES.find(([key]) => key === note.decisionType) || [])[1] : null;
    const who = note && (note.authorName && note.authorName === user?.name ? 'Du' : note.authorName);
    const tab3 = detailSections?.tab3 && !mergedTab3 ? detailSections.tab3 : null;
    const tab3Count = !tab3 ? null
      : tab3.kind === 'members' ? (tab3.owners?.length || 0) + (tab3.members?.length || 0)
      : tab3.kind === 'permissions' ? (tab3.permissionCount ?? tab3.permissions?.length ?? null)
      : tab3.kind === 'lists' ? tab3.sections.reduce((n, s) => n + s.items.length, 0)
      : tab3.kind === 'devicePolicies' ? (tab3.overview?.total ?? null)
      : null;
    const flagged = (f) => f === true || f === 'gap';
    const sortedFacts = [...factRows].sort((x, y) => Number(flagged(y[3])) - Number(flagged(x[3])));
    const cards = [{
      key: 'evidens',
      title: factsTitleFor(selected, selectedItem),
      count: sortedFacts.length,
      defaultOpen: sortedFacts.some((r) => flagged(r[3])),
      body: (
        <>
          {sortedFacts.length > 0 ? (
            <div className="pn-list">
              {sortedFacts.map(([label, value, , flag]) => (
                <div className="pn-row" key={label}>
                  <span>{label}</span>
                  <b className={flag === 'gap' ? 'pn-v--bad' : flag === true ? 'pn-v--warn' : undefined}>{value}</b>
                </div>
              ))}
            </div>
          ) : <div className="pn-pad"><p className="dp-muted">Ingen yderligere fakta om dette objekt.</p></div>}
          {cleanup && <div className="pn-pad"><CleanupNote policy={cleanup.policy} /></div>}
        </>
      ),
    }];
    const note3 = (text) => <div className="pn-pad"><p className="dp-muted">{text}</p></div>;
    if (tab3) {
      const kind = detailSections.tab3.kind;
      const t3 = detailSections.tab3;
      if (kind === 'permissions') {
        const perms = (t3.permissions || []).map((p) => ({ ...p, level: permissionLevel(p) }));
        if (perms.length === 0) cards.push({ key: 'tab3', title: 'Tilladelser', count: t3.permissionCount ?? null, body: note3((t3.permissionCount ?? 0) > 0 ? 'Tilladelsernes navne kunne ikke hentes fra Microsoft lige nu, prøv igen senere.' : 'Appen har ingen tilladelser.') });
        let first = true;
        PERM_LEVELS.forEach(([lv, label, tone]) => {
          const list = perms.filter((p) => p.level === lv);
          if (!list.length) return;
          cards.push({ key: `perm-${lv}`, title: label, count: list.length, tone, defaultOpen: first, body: <PanelItems items={list.map((p) => ({ name: p.name, mono: true, desc: lv === 'other' ? p.description : permissionDescription(p.name) }))} /> });
          first = false;
        });
      } else if (kind === 'members') {
        const people = (m) => ({ name: m.displayName, tag: m.userPrincipalName || m.mail || undefined });
        const owners = t3.hideOwners ? [] : (t3.owners || []);
        const everyone = t3.members || [];
        const guests = everyone.filter((m) => m.isGuest);
        const regular = everyone.filter((m) => !m.isGuest);
        if (owners.length + everyone.length === 0) cards.push({ key: 'tab3', title: TAB3_LABEL[selected.area] || 'Medlemmer', count: 0, body: note3('Ingen registreret.') });
        if (guests.length) cards.push({ key: 'mem-g', title: 'Gæster udefra', count: guests.length, tone: 'warn', defaultOpen: true, body: <PanelItems items={guests.map(people)} /> });
        if (owners.length) cards.push({ key: 'mem-o', title: 'Ejere', count: owners.length, body: <PanelItems items={owners.map(people)} /> });
        if (regular.length) cards.push({ key: 'mem-m', title: t3.membersLabel || 'Medlemmer', count: regular.length, body: <PanelItems items={regular.map(people)} /> });
      } else if (kind === 'lists') {
        const filled = t3.sections.filter((section) => section.items.length > 0);
        if (filled.length === 0) cards.push({ key: 'tab3', title: TAB3_LABEL[selected.area] || 'Detaljer', count: 0, body: note3('Ingen registreret.') });
        filled.forEach((section, i) => cards.push({ key: `list-${i}`, title: section.label, count: section.items.length, body: <PanelItems items={section.items.map(([name, meta]) => ({ name, tag: meta ? String(meta) : undefined }))} /> }));
      } else if (kind === 'access') {
        const shown = t3.sections.filter((section) => section.missing || section.items.length > 0);
        if (shown.length === 0) cards.push({ key: 'tab3', title: TAB3_LABEL[selected.area] || 'Adgang', count: 0, body: note3('Ingen registreret.') });
        shown.forEach((section, i) => cards.push({ key: `acc-${i}`, title: section.label, count: section.missing ? null : section.items.length, body: section.missing ? note3('Ikke tilgængeligt endnu') : <PanelItems items={section.items.map((it) => ({ name: it.name, tag: it.sub || undefined }))} /> }));
      } else if (kind === 'rule') {
        cards.push({ key: 'tab3', title: 'Reglen', count: null, tone: selected.tone === 'bad' ? 'bad' : selected.tone === 'warn' ? 'warn' : undefined, defaultOpen: true, body: <div className="pn-pad"><RulePanel key={selectedItem.id} item={t3.item} /></div> });
      } else if (kind === 'devicePolicies') {
        const { state, overview } = t3;
        const openPolicy = (p) => navigate(String(p.key).startsWith('device:') ? `/tenant/${tenant.tenantId}/configuration` : `/tenant/${tenant.tenantId}/configuration?policy=${encodeURIComponent(`pol:${p.key}`)}`);
        if (state === 'loading') cards.push({ key: 'tab3', title: 'Politikker', count: null, body: note3('Henter politikker…') });
        else if (state === 'missing' || !overview) cards.push({ key: 'tab3', title: 'Politikker', count: null, body: <div className="pn-pad"><DataBlock rows={[['Resultat', NOT_AVAILABLE, false, 'gap']]} /></div> });
        else if (overview.total === 0) cards.push({ key: 'tab3', title: 'Politikker', count: 0, body: note3('Der er ikke hentet politikresultater for enheden endnu.') });
        else {
          // Every policy assigned to the device, whatever its status: what needs fixing is open, the rest closed.
          const items = (list) => list.map((p) => ({ name: p.name, tag: p.category || undefined, onClick: () => openPolicy(p) }));
          const nothingElse = overview.problems.length === 0 && overview.pendings.length === 0;
          if (overview.problems.length > 0) cards.push({ key: 'pol-bad', title: 'Kræver rettelse', count: overview.problems.length, tone: 'bad', defaultOpen: true, body: <PanelItems items={items(overview.problems)} /> });
          if (overview.pendings.length > 0) cards.push({ key: 'pol-warn', title: 'Afventer', count: overview.pendings.length, tone: 'warn', body: <PanelItems items={items(overview.pendings)} /> });
          if (overview.oks.length > 0) cards.push({ key: 'pol-ok', title: 'Opfyldt', count: overview.oks.length, tone: 'ok', defaultOpen: nothingElse, body: <PanelItems items={items(overview.oks)} limit={nothingElse ? 8 : 3} /> });
        }
      } else {
        cards.push({ key: 'tab3', title: TAB3_LABEL[selected.area] || 'Detaljer', count: tab3Count, body: <div className="pn-pad"><DataBlock rows={kvRows(t3.rows)} /></div> });
      }
    }
    const idOf = (c) => `card:${c.title}`;
    const openOf = (c) => isCardOpen(idOf(c), !!c.defaultOpen);
    const allCardsOpen = cards.every(openOf);
    // "Hvad er det" is the explanation of the whole group of findings; it is open until the user closes it.
    const WHAT_ID = 'card:Hvad er det';
    const whatOpen = isCardOpen(WHAT_ID, true);
    return (
      <div className="pn">
        <div className="pn-h pn-h--dot">
          <div className="pn-h__t">
            <h3 title={selectedItem.name}>{selectedItem.name}</h3>
            <p>{[...new Set([selectedItem.sub, selected.title].filter(Boolean))].join(' · ')}</p>
          </div>
          <span className={`pn-dot pn-dot--${toneKey}`} title={toneText} aria-label={toneText} />
        </div>
        <div className="pn-body" ref={detailBodyRef}>
          {isOk && <PanelAlert tone="ok">{selected.why || selected.hint}</PanelAlert>}
          {warnings.map((w) => <PanelAlert key={w} tone={selected.tone === 'warn' ? 'warn' : 'bad'}>{w}</PanelAlert>)}
          {advice && <PanelRec>{advice}</PanelRec>}
          {/* A domain whose DMARC is on quarantine is in order (it enforces); reject is the strictest, said as a quiet next step. */}
          {String(selectedItem.domainAuth?.dmarc?.policy || '').toLowerCase() === 'quarantine' && (
            <PanelRec>
              Skift DMARC-politikken fra quarantine til reject. I dag lægges mail, der ikke består DMARC-tjekket (SPF eller DKIM passer ikke til jeres domæne), i spam. Med reject afvises den helt. Skift, når DMARC-rapporterne viser, at al jeres egen mail, også fra nyhedsbreve og andre tjenester, består tjekket.
            </PanelRec>
          )}
          {selected.about && (
            <PanelCard title="Hvad er det" tone="what" open={whatOpen} onToggle={() => toggleCard(WHAT_ID, whatOpen)}>
              <div className="pn-pad"><p className="pn-prose">{selected.about}</p></div>
            </PanelCard>
          )}
          {!isOk && <FixPath findingKey={selected.key} />}

          <div className="pn-dh">
            <span>Detaljer</span>
            {cards.length > 1 && <ExpandAll allOpen={allCardsOpen} onClick={() => setCardsOpen(cards.map(idOf), !allCardsOpen)} />}
          </div>
          {cards.map((c) => (
            <PanelCard key={c.key} title={c.title} count={c.count} tone={c.tone} open={openOf(c)} onToggle={() => toggleCard(idOf(c), openOf(c))}>{c.body}</PanelCard>
          ))}
        </div>

        {/* The decision is made on the card; the panel only shows one that has been made. */}
        {decided && (
          <div className="pn-foot">
            <div className="pn-foot__r"><span>Beslutning</span></div>
            <div className="pn-foot__s">
              <DecisionTag badge={getStatusBadge(note)} /><small>{[who, new Date(note.createdAt).toLocaleDateString('da-DK')].filter(Boolean).join(' · ')}</small>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`arbejdsko-page${focusOn && viewMode === 'fund' ? ' is-focus' : ''}`} ref={pageRef} style={{ '--pw': `${detailPanel.width}px`, ...(toolsRight != null ? { '--tools-right': `${toolsRight}px` } : {}) }}>
      {renderDecisionModal()}
      {/* Microsoft-indstillinger now aligns with the page title itself
          (.head is already a space-between flex row shared across pages) —
          right edge, roughly over .detail-panel-col below — instead of the
          Områder tabs row underneath it. */}
      <div className="head">
        <div>
          <div className="eye">{areaFilter === 'alle' ? 'Gennemgang' : `Gennemgang - ${MS365_AREA_NAME[areaFilter] || ''}`}</div>
          <h1>Hvad kræver jeres opmærksomhed lige nu?</h1>
          <p>Det, Microsoft 365 har fundet, og som I skal tage stilling til. Prioriteret på tværs af hele Microsoft 365.</p>
        </div>
        <div className="pg-tools">
          <span className="wq-vt__lbl">Visning</span>
          <div className="wq-vt" role="group" aria-label="Visning">
            <button type="button" className={`wq-vt__btn${viewMode === 'fund' ? ' is-on' : ''}`} title="Fundene samlet i kort efter prioritet" aria-label="Prioritet" aria-pressed={viewMode === 'fund'} onClick={() => setViewMode('fund')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /></svg>
              <span>Prioritet</span>
            </button>
            <button type="button" className={`wq-vt__btn${viewMode === 'alle' ? ' is-on' : ''}`} title="Alle objekter i en tabel, med kolonner" aria-label="Liste" aria-pressed={viewMode === 'alle'} onClick={() => setViewMode('alle')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M3 15h18M9 4v16" /></svg>
              <span>Liste</span>
            </button>
          </div>
          <FocusToggle on={focusOn} onToggle={toggleFocus} disabled={viewMode !== 'fund'} disabledTitle="Focus mode bruges kun i visningen Prioritet" />
        </div>
      </div>

      <div className="workspace">
        <div className="list-panel wq-menu list-panel--float">
          {/* Områder now lives inside the same warm-white box as the status tabs below it (Alle/
              Kræver handling/Bør gennemgås) instead of its own separate strip above .workspace —
              per direct feedback, two stacked boxes read as two different things, not one filter
              group. */}
          <div className="wq-menu__card mc-frame">
          <TileMenu
            label="Områder"
            className="tm--areas"
            tiles={[
              // Alle objekter has per-item columns that don't exist across 9 different area types, so "Alle" is greyed out
              // (not removed) in that mode.
              { key: 'alle', name: 'Alle', n: totalFindings, bad: totalBad, off: viewMode !== 'fund', icon: 'grid', active: areaFilter === 'alle', onClick: () => { if (viewMode === 'fund') setAreaFilter('alle'); } },
              ...AREA_KEYS.map((key) => ({ key, name: areaLabel(key), n: (areaCounts[key]?.bad || 0) + (areaCounts[key]?.warn || 0), bad: areaCounts[key]?.bad || 0, off: false, icon: AREA_ICON[key] || 'grid', active: areaFilter === key, onClick: () => setAreaFilter(key) })),
            ].map((x) => {
              return {
                key: x.key,
                name: x.name,
                bg: AREA_TILE_COLOR[x.key][0],
                fg: AREA_TILE_COLOR[x.key][1],
                icon: <NcIcon name={x.icon} size={19} />,
                // What needs action first; with none, what should be looked at; only when both are zero is it in order.
                status: x.bad ? { text: `${x.bad} kræver handling`, tone: 'bad' } : x.n ? { text: `${x.n} bør gennemgås`, tone: 'warn' } : { text: 'I orden', tone: 'ok' },
                active: x.active,
                off: x.off,
                onClick: x.onClick,
              };
            })}
          />
          {/* Without Entra ID P2 / Governance there is no PIM, so there are no eligible assignments to miss: not an error. */}
          {areaFilter === 'roles' && risks?.privilegedRolesEligibilityWarning && !/needs to have|P2|Governance license/i.test(risks.privilegedRolesEligibilityWarning) && (
            <p className="state-error">
              Kunne ikke hente PIM-berettigede rolletildelinger — listen herunder mangler muligvis personer der kun aktiverer en rolle via PIM. ({risks.privilegedRolesEligibilityWarning})
            </p>
          )}
          <div className="list-panel__filter">
            {/* Status: one segmented switch in a sunken track, no numbers (the area tabs above carry the counts). */}
            <div className="wq-seg" role="group" aria-label="Status">
              <button type="button" className={`wq-seg__btn${statusFilter === 'alle' ? ' is-on' : ''}`} onClick={() => setStatusFilter('alle')}>Alle</button>
              <button type="button" className={`wq-seg__btn${statusFilter === 'bad' ? ' is-on' : ''}`} onClick={() => setStatusFilter('bad')}><span className="wq-seg__dot wq-seg__dot--bad" />Kræver handling</button>
              <button type="button" className={`wq-seg__btn${statusFilter === 'warn' ? ' is-on' : ''}`} onClick={() => setStatusFilter('warn')}><span className="wq-seg__dot wq-seg__dot--warn" />Bør gennemgås</button>
              <button type="button" className={`wq-seg__btn${statusFilter === 'ok' ? ' is-on' : ''}`} onClick={() => setStatusFilter('ok')}><span className="wq-seg__dot wq-seg__dot--ok" />I orden</button>
            </div>
            <div className="wq-menu__right">
                            {viewMode === 'fund' && visibleGroups.length > 0 && (
                <ExpandAll allOpen={allFundOpen} onClick={toggleAllFund} />
              )}
              {viewMode === 'alle' && (
                <div className="tabel-export" ref={tabelExportMenuRef}>
                  <button
                    type="button"
                    className="tabel-export__btn"
                    disabled={!license.features.dataExport}
                    title={license.features.dataExport ? 'Eksportér' : 'Eksport er ikke med i en prøveperiode'}
                    aria-label="Eksportér"
                    onClick={() => setTabelExportMenuOpen((o) => !o)}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12M7 10l5 5 5-5M4 20h16" /></svg>
                  </button>
                  {tabelExportMenuOpen && (
                    <div className="tabel-export__menu">
                      <button type="button" onClick={handleTabelExportExcel} disabled={tabelExporting}>Excel</button>
                      <button type="button" onClick={handleTabelExportPdf} disabled={tabelExporting}>
                        {tabelExporting ? 'Genererer…' : 'PDF'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <SearchInput value={search} onChange={setSearch} placeholder="Søg..." />
            </div>
          </div>
          </div>
          {viewMode === 'fund' && deviceNotFinding && (
            <div className="wq-policy-filter">
              Enheden er ikke et fund.
              <button type="button" className="wq-policy-link" onClick={() => setViewMode('alle')}>Se den under Liste</button>
            </div>
          )}
          {viewMode === 'fund' && pimMissing && (
            <section className={`wqc wqc--rec wq-rec${pimFolded ? ' is-folded' : ''}`}>
              <header className="wqc__h"><span className="rec__ic"><NcIcon name="info" size={18} /></span><h3>Anbefaling: brug PIM til administratorroller</h3><button type="button" className="rec__x" onClick={togglePim} aria-expanded={!pimFolded} title={pimFolded ? 'Vis anbefalingen' : 'Fold anbefalingen sammen'} aria-label={pimFolded ? 'Vis anbefalingen' : 'Fold anbefalingen sammen'}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: pimFolded ? 'none' : 'rotate(180deg)' }}><path d="M6 9l6 6 6-6" /></svg></button></header>
              {!pimFolded && <div className="rec__b">
                <p>Jeres tenant har ikke Microsoft Entra ID P2, så administratorroller er tildelt permanent. Med PIM (Privileged Identity Management) får en administrator kun rollen, når den skal bruges, og kun for en kort periode.</p>
                <p className="rec__s">PIM kræver Microsoft Entra ID P2 eller Entra ID Governance.</p>
              </div>}
            </section>
          )}
          {viewMode === 'fund' && statusFilter === 'ok' ? (
            visibleOk.length === 0 ? (
              <div className="state-empty"><p>Intet at vise her endnu. Vi viser kun det, vi kan tælle.</p></div>
            ) : (
              <>
                <div className="list-panel__gap" />
                <div className="list-panel__body wq-tray wq-tray--cards">
                  {renderOkFrame(false)}
                </div>
              </>
            )
          ) : viewMode === 'fund' ? (
            visibleGroups.length === 0 ? (
              statusFilter === 'alle' && !search.trim() ? (
                <>
                  <div className="wq-ok">
                    <span className="wq-ok__ic"><PortalIcon name="check" size={26} /></span>
                    <b>Alt er i orden her</b>
                    <p>Der er ingen fund i {areaFilter === 'alle' ? 'Microsoft 365' : areaLabel(areaFilter)} lige nu.</p>
                  </div>
                  {visibleOk.length > 0 && <div className="list-panel__body wq-tray wq-tray--cards">{renderOkFrame(true)}</div>}
                </>
              ) : (
                <div className="state-empty"><p>{!search.trim() && (statusFilter === 'bad' || statusFilter === 'warn') ? 'Godt arbejde! Intet her, lad os holde det sådan.' : 'Ingen fund matcher filteret.'}</p></div>
              )
            ) : (
              <>
                <div className="list-panel__gap" />
                <div className={`list-panel__body wq-tray wq-tray--cards${focusOn && selected ? ' has-sel' : ''}`}>
                  {/* One frame per status, red first, then orange, then the rest; a frame can be folded. */}
                  {[['bad', 'Kræver handling'], ['warn', 'Bør gennemgås'], ['other', 'Til gennemsyn']].map(([tone, title]) => {
                    const gs = visibleGroups.filter((g) => toneOf(g) === tone);
                    if (gs.length === 0) return null;
                    const n = gs.reduce((sum, g) => sum + (g.items ? openIn(g) : 0), 0);
                    const settled = gs.reduce((sum, g) => sum + (g.items ? settledIn(g) : 0), 0);
                    return (
                      <StatusFrame key={tone} tone={tone === 'other' ? 'neutral' : tone} title={title} count={n} extra={settled > 0 ? `${settled} afgjort` : null} onExtra={() => navigate(`/tenant/${routeTenantId}/decisions`)} folded={collapsedTones.has(tone)} onToggle={() => setCollapsedTones((prev) => { const next = new Set(prev); if (next.has(tone)) next.delete(tone); else next.add(tone); return next; })}>
                        {gs.filter((g) => openIn(g) > 0).map(renderFundRow)}
                      </StatusFrame>
                    );
                  })}
                  {statusFilter === 'alle' && visibleOk.length > 0 && renderOkFrame(true)}
                </div>
              </>
            )
          ) : tabelFilteredItems.length === 0 ? (
            <div className="state-empty">
              <p>{policyFilter ? (missingFilter ? `Ingen enheder er uden for «${policyFilter}», så vidt vi kan læse.` : `Ingen enheder har lige nu «${policyFilter}» som ikke opfyldt.`) : 'Ingen objekter i dette område.'}</p>
              {policyFilter && <button type="button" className="wq-policy-link" onClick={() => setSearchParams((prev) => { const m = new URLSearchParams(prev); m.delete('policy'); return m; }, { replace: true })}>Vis alle enheder</button>}
            </div>
          ) : (
            <div className="wq-tablecard">
              {policyFilter && (
                <div className="wq-policy-filter">
                  {missingFilter ? <>Viser enheder, som <b>{policyFilter}</b> ikke omfatter. Intunes eget tal kan afvige lidt, fordi det opdateres senere.</> : <>Viser enheder, hvor <b>{policyFilter}</b> ikke er opfyldt.</>}
                  <button type="button" className="wq-policy-link" onClick={() => setSearchParams((prev) => { const m = new URLSearchParams(prev); m.delete('policy'); return m; }, { replace: true })}>Ryd filter</button>
                </div>
              )}
              <div className="list-panel__head">
                <span>{tabelFilteredItems.length} objekter</span>
              </div>
              <div className="tabel-head-row">
                <div className="tabel-head-row__main">
                  {[
                    ['name', 'Navn'],
                    ...(areaFilter === 'alle' ? [['area', 'Område']] : tabelMetaColumns.map((label) => [`meta:${label}`, label])),
                  ].map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      className={`entity-row__sortbtn${tabelSort.key === key ? ' is-active' : ''}${key.startsWith('meta:') ? ' is-movable' : ''}${overCol === label && dragCol && dragCol !== label ? ' is-dragover' : ''}`}
                      style={{ flex: colFlex(key === 'name' ? 'name' : label) }}
                      onClick={() => setTabelSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))}
                      {...(key.startsWith('meta:') ? {
                        draggable: true,
                        title: 'Træk for at flytte kolonnen',
                        onDragStart: (e) => { setDragCol(label); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', label); } catch { /* some browsers */ } },
                        onDragOver: (e) => { if (dragCol) { e.preventDefault(); setOverCol(label); } },
                        onDragLeave: () => setOverCol((c) => (c === label ? null : c)),
                        onDrop: (e) => { e.preventDefault(); moveColumn(dragCol, label); setDragCol(null); setOverCol(null); },
                        onDragEnd: () => { setDragCol(null); setOverCol(null); },
                        onKeyDown: (e) => { if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) { e.preventDefault(); nudgeColumn(label, e.key === 'ArrowLeft' ? -1 : 1); } },
                      } : {})}
                    >
                      {label}
                      {tabelSort.key === key && <span className="entity-row__sortarrow">{tabelSort.dir === 'asc' ? '↑' : '↓'}</span>}
                      <span className="tabel-resize" draggable={false} title="Træk for at ændre bredden. Dobbeltklik nulstiller." onMouseDown={(e) => startResize(e, key === 'name' ? 'name' : label)} onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => { e.stopPropagation(); resetWidth(key === 'name' ? 'name' : label); }} />
                    </button>
                  ))}
                </div>
                <span className="entity-row__sortbtn tabel-head-row__dec" style={{ cursor: 'default' }}>Beslutning</span>
                <button
                  type="button"
                  className={`entity-row__sortbtn tabel-head-row__status${tabelSort.key === 'status' ? ' is-active' : ''}`}
                  onDragOver={(e) => { if (dragCol) e.preventDefault(); }}
                  onDrop={(e) => { e.preventDefault(); moveColumn(dragCol, null); setDragCol(null); setOverCol(null); }}
                  onClick={() => setTabelSort((s) => (s.key === 'status' ? { key: 'status', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: 'status', dir: 'asc' }))}
                >
                  Status
                  {tabelSort.key === 'status' && <span className="entity-row__sortarrow">{tabelSort.dir === 'asc' ? '↑' : '↓'}</span>}
                </button>
              </div>
              <div className="list-panel__body">
                {(tabelShowAll ? tabelFilteredItems : tabelFilteredItems.slice(0, TABEL_PAGE_SIZE)).map((item) => {
                  const tone = tabelToneFor(item);
                  const isSelected = tabelSelectedItemRaw?.area === item.area && tabelSelectedItemRaw?.id === item.id;
                  return (
                    <div
                      key={`${item.area}:${item.id}`}
                      className={`tabel-row${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setTabelSelectedObj(item)}
                    >
                      <div className="tabel-row__main">
                        <div className="tabel-row__col" style={{ flex: colFlex('name') }} title={item.sub ? `${item.name} — ${item.sub}` : item.name}>
                          <b>{item.name}</b>
                          {/* Type has its own column here — repeating it as a subtitle is noise. */}
                          {!(areaFilter === 'sharepoint' && TABEL_COLUMNS.sharepoint.includes('Type')) && <small>{item.sub}</small>}
                        </div>
                        {areaFilter === 'alle'
                          ? (
                            <div className="tabel-row__col" style={{ flex: colFlex('Område') }}>
                              <b>{areaTabLabel(item.area)}</b>
                            </div>
                          )
                          : tabelMetaColumns.map((label) => {
                            const value = item.meta?.find(([l]) => l === label)?.[1] ?? '—';
                            return (
                              <div className="tabel-row__col" style={{ flex: colFlex(label) }} key={label} title={value}>
                                <b>{value}</b>
                              </div>
                            );
                          })}
                      </div>
                      <span className="tabel-row__dec-col">
                        {(() => { const gk = tabelToneMap.get(`${item.area}:${item.id}`)?.key; const nt = gk ? findNote(item.id, gk) : null; return nt?.decisionType ? <DecisionTag badge={getStatusBadge(nt)} /> : null; })()}
                      </span>
                      <span className="tabel-row__status-col">
                        <span className={`fund-row__sev fund-row__sev--${tone}`}>{TABEL_STATUS_LABEL[tone]}</span>
                      </span>
                    </div>
                  );
                })}
                {!tabelShowAll && tabelFilteredItems.length > TABEL_PAGE_SIZE && (
                  <button type="button" className="fund-row__show-all" onClick={() => setTabelShowAll(true)}>
                    Vis alle {tabelFilteredItems.length} objekter →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

          <div className={`resize-handle${detailPanel.dragging ? ' is-dragging' : ''}`} onMouseDown={detailPanel.onMouseDown} />

          <div className="detail-panel-col" style={{ width: detailPanel.width }}>
              <div className="detail-panel">
                {multiMode && (
                  <div className="pn">
                    <div className="pn-h">
                      <h3>{pickedItems.length} {pickedItems.length === 1 ? 'objekt' : 'objekter'} valgt</h3>
                      <p>{findingTitle(selected.key) || selected.title}</p>
                    </div>
                    <div className="pn-body" ref={detailBodyRef}>
                      <ul className="mp-list">
                        {pickedItems.map((item) => (
                          <li key={item.id}>
                            <span>{item.name}</span>
                            <button type="button" aria-label={`Fjern ${item.name} fra valget`} onClick={() => setPicked((prev) => { const next = new Set(prev); next.delete(item.id); return next; })}>×</button>
                          </li>
                        ))}
                      </ul>
                      <p className="pn-hint">Beslutningen gemmes på hvert objekt. Tryk på Træf beslutning under fundet.</p>
                    </div>
                  </div>
                )}
                {!multiMode && !selectedItem && (
                  <div className="wq-empty">
                    <div className="wq-empty__slot">
                      <span className="wq-empty__ic" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg></span>
                      <b>{viewMode === 'fund' ? 'Vælg et fund' : 'Vælg et objekt'}</b>
                      <small>Forklaring, anbefaling og fakta vises her.</small>
                    </div>
                  </div>
                )}
                {!multiMode && selectedItem && renderPanel()}
              </div>
            </div>
          </div>
    </div>
  );
}
